import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type {
  TrailObservationCollection,
  WatershedContextDoc,
} from '../lib/trail-types';
import { useAuth } from '../lib/session';
import { QueueBadge } from '../components/QueueBadge';
import { LabelQueueBadge } from '../components/LabelQueueBadge';
import { AccountCornerButton } from '../components/AccountCornerButton';
import {
  consumeJustSubmittedVersion,
  markSeenOnMap,
  observationIdsFor,
  readRecentSubmissions,
} from '../lib/recent-submissions';
import {
  fetchTrailManifest,
  fetchTrailWatersheds,
  fetchTrailObservations,
  fetchTrailWatershedContext,
  fetchObservationDetail,
  type TrailManifest,
} from '../lib/api';
import {
  buildWatershedPopupCopy,
  countRecords,
  countUnassignedInMapped,
  membersOfWatershed,
  pointInPolygonGeometry,
} from '../lib/region-count';
import { buildDc311PopupHtml } from '../lib/dc311-popup';
import { formatIngestedDate, olmWhenLine } from '../lib/olm-popup';
import { SOURCE_STYLE, sourceKey, rendersOnMap } from '../lib/source-style';
import {
  WATER_BODY_LINE,
  WATER_STATUS_COLOR,
  WATER_STATUS_INK,
  WATER_STATUS_SHORT,
  WATER_STATUS_STROKE,
  waterStatusOf,
  type ImpairmentDoc,
  type WaterStatus,
} from '../lib/water-status';
import {
  type TimeWindow,
  ageBucketClassName,
  isUndated,
} from '../lib/time-window';
import { computeYearBars, pastVsPriorYear, buildYearBarHtml } from '../lib/year-bars';
import { parseMapStateFromUrl, writeMapStateToUrl } from '../lib/map-url-state';
import { tilesConfigOf, warmTileHeaders, type TrailTilesConfig } from '../lib/tile-mode';
import type { ParkFeatureLike } from '../lib/mission-select';
import type { ReadoutStats } from '../lib/readout-count';
import type ReadoutBarComponent from '../components/ReadoutBar';
import type ParkChipComponent from '../components/ParkChip';

/** Locate-button states. 'denied'/'unavailable' are informative, not errors. */
type LocateState = 'off' | 'locating' | 'on' | 'denied' | 'unavailable';
import { commonsConfigOf, type TrailCommonsConfig } from '../lib/commons-mode';
import { readOwnPinStubs } from '../lib/own-pins';
import { readReportedPinIds } from '../lib/reported-pins';
import {
  buildGenericObsPopupHtml,
  buildWsBadgeHtml,
  buildWatershedPopupShellHtml,
  buildCleanupLedgerHtml,
  wireCleanupVerbButtonIn,
  relativeAgeString,
  stateFullName,
} from '../lib/obs-popup';
import { estimateRate, estimateGoalGradient } from '../lib/cleanup-estimator';
// Issue #69: dated same-scene history (OLM photo-date accumulation
// windows) → estimator inputs, honesty rules delegated to time-window.ts.
// The post-log mutation half (cleanup-optimistic.ts) is dynamic-imported
// at claim time so the entry chunk never carries it (§10 budget).
import { estimatorInputForFeature, featureItemCount } from '../lib/scene-history';
import type { CleanupEvent } from '../lib/cleanup-types';
// The verb flow itself (bucket picker, org selector + volunteer fields,
// logCleanupEvent, after-photo step) lives in the SHARED lib/cleanup-verb
// module (issue #78) — dynamic-imported at verb-click via obs-popup's
// wireCleanupVerbButtonIn, so neither it nor org-select rides the entry
// chunk. Type-only import: erased at build time.
import type { CleanupLoggedInfo, CleanupVerbHost } from '../lib/cleanup-verb';
import { ensureLeafletLoaded } from '../lib/leaflet-loader';
// Type-only imports — erased at build time, so the maplibre chunk (and
// with it firefly-gl / firefly-data / firefly-style, ~10 KB of source and
// two inline shaders) stays lazy and the 40 KB entry gate never pays for
// any of it. MapTimeControls is dynamic-imported for the same reason.
import type {
  ActiveWatershedInfo,
  ActiveParkInfo,
  FireflyStats,
  TileExploreMapHandle,
} from '../lib/explore-map-tiles';
import type { AdaptedObservationFeature } from '../lib/tile-feature-adapter';
import type MapTimeControlsComponent from '../components/MapTimeControls';
import {
  INTRO_AUTO_OPEN_DELAY_MS,
  readIntroSeen,
  shouldAutoOpenIntro,
  writeIntroSeen,
} from '../lib/intro-gate';

// Mid-Atlantic + DC + Massaponax + close regional bbox. Drops the
// Norfolk/Tidewater OLM regionals (~50 pins) that were inflating the
// initial-load payload to ~1.2MB. Covers the founder's landing area
// (DC metro), Massaponax, Baltimore, Annapolis, and the close-in
// regional clusters that matter for the demo.
//
// Long term (Q8 ~5k-obs threshold) this becomes per-viewport bbox +
// tile fetches.
const ALL_DATA_BBOX = { w: -78.5, s: 37.8, e: -75.8, n: 39.6 };

/**
 * Fetch the observations + watersheds for a given manifest version and
 * derive the legacy single-HUC state shape (watershed FC, ctx doc,
 * impairment doc) from the multi-HUC watersheds payload. Used by both
 * the initial-load effect and the version-bump poll branch.
 */
async function loadVersion(
  manifest: TrailManifest,
  opts: { preferHuc12?: string } = {},
): Promise<{
  observations: TrailObservationCollection;
  watershedFC: any;
  ctxDoc: WatershedContextDoc | null;
  impairmentDoc: ImpairmentDoc | null;
}> {
  const [watershedsFC, observationsFC] = await Promise.all([
    fetchTrailWatersheds(manifest.version),
    // verified=all so the client-side toggle determines what to render;
    // keeps cache key stable across users with different toggle states.
    fetchTrailObservations(manifest.version, ALL_DATA_BBOX, { verified: 'all' }),
  ]);

  // Primary watershed selection:
  //   1. If the caller passed preferHuc12 (e.g., ?huc= in the URL),
  //      pick that one exactly — gives shared links a deterministic ctx.
  //   2. Otherwise, pick the watershed whose centroid is closest to the
  //      initial map landing (DC metro). Multi-HUC dynamic pill on pan
  //      is still trail #24 territory.
  let primary: typeof watershedsFC.features[number] | null = null;
  if (opts.preferHuc12) {
    primary = watershedsFC.features.find(
      (f) => (f.properties as { huc12?: string }).huc12 === opts.preferHuc12,
    ) ?? null;
  }
  if (!primary) {
    let primaryDist = Infinity;
    for (const f of watershedsFC.features) {
      const c = (f.properties as Record<string, unknown>).centroid as
        { lon?: number; lat?: number } | undefined;
      if (!c?.lon || !c?.lat) continue;
      const d = (c.lat - DEFAULT_MAP_LANDING.lat) ** 2
              + (c.lon - DEFAULT_MAP_LANDING.lon) ** 2;
      if (d < primaryDist) {
        primaryDist = d;
        primary = f;
      }
    }
  }
  if (!primary) primary = watershedsFC.features[0];

  let ctxDoc: WatershedContextDoc | null = null;
  let impairmentDoc: ImpairmentDoc | null = null;
  if (primary) {
    const props = primary.properties as Record<string, unknown>;
    const wsCtx = props.watershed_context as WatershedContextDoc | undefined;
    const impStat = props.impairment_status as ImpairmentDoc | undefined;
    if (wsCtx) ctxDoc = wsCtx;
    if (impStat) impairmentDoc = impStat;
  }

  // Render ALL watershed polygons (16 today: Massaponax + 15 DC HUCs).
  // L.geoJSON iterates every feature, so passing the full FC paints
  // each polygon with the configured style.
  const watershedFC = watershedsFC;

  return {
    observations: observationsFC as unknown as TrailObservationCollection,
    watershedFC,
    ctxDoc,
    impairmentDoc,
  };
}

// Persistent client-side toggle (Q4 from the Aurora migration proposal):
// default = verified-only; toggle reveals unverified pins AND includes
// them in aggregate counts (the watershed-pill sentence numbers respond
// to it). Persisted per-browser via localStorage so a contributor doesn't
// re-tick every visit.
const STORAGE_KEY_SHOW_UNVERIFIED = 'trail.show_unverified';
function readShowUnverified(): boolean {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(STORAGE_KEY_SHOW_UNVERIFIED) === 'true';
}
function writeShowUnverified(v: boolean): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY_SHOW_UNVERIFIED, v ? 'true' : 'false');
}

// Manifest poll cadence. Adaptive per proposal Q10 — tight while the tab
// has focus (post-submission visibility within seconds), relaxed when
// hidden. At current scale the 5s baseline is what gives the submission
// SLA: the contributor's own optimistic-poll fires immediately on 201,
// other clients see the new pin on their next poll.
const MANIFEST_POLL_INTERVAL_FOCUSED_MS = 5000;
const MANIFEST_POLL_INTERVAL_HIDDEN_MS = 10000;

// Default initial map view if the URL has no ?z=&c= state. Chosen to
// land on the DC metro where the bulk of OLM data lives (154/154 OLM
// clusters are in DMV per the v1 dataset measurement).
const DEFAULT_MAP_LANDING = { lat: 38.95, lon: -77.05, zoom: 10 };

// URL-state (?z=&c=&huc=) helpers moved to src/lib/map-url-state.ts —
// shared with the MapLibre tile-mode controller. Behavior unchanged
// except the z floor (5 → 3) so national-view deep links round-trip;
// Leaflet clamps to its own minZoom on setView.

// Ray-casting point-in-polygon moved to src/lib/region-count.ts (shared
// with the popup/pill/badge counting helpers) — behavior unchanged. Used
// by the dynamic-pill handler to swap ctx as the user pans — closes #24.

/**
 * Find the watershed feature (if any) whose polygon contains the given
 * point. First match wins — HUC-12s don't overlap in practice.
 */
function findContainingWatershed(
  lat: number,
  lon: number,
  watershedsFC: { features: Array<{ geometry: { type: string; coordinates: unknown }; properties: Record<string, unknown> }> } | null,
): { feature: { properties: Record<string, unknown> }; huc12: string } | null {
  if (!watershedsFC) return null;
  for (const f of watershedsFC.features) {
    if (pointInPolygonGeometry(lat, lon, f.geometry)) {
      const huc12 = String((f.properties as { huc12?: string }).huc12 ?? '');
      if (huc12) return { feature: f, huc12 };
    }
  }
  return null;
}

/**
 * Trash Trail PUBLIC consumption surface.
 *
 * Post-geolocation view for HUC-12 020801040103 (Massaponax Creek).
 * All rendered data is sourced from named federal APIs (USGS WBD, EPA
 * ATTAINS, EPA Water Quality Portal) via scripts/ingest.mjs. No
 * fabricated observations.
 *
 * Map lib: Leaflet via CDN (see index.html). Swap to bundled maplibre-gl
 * once the pnpm-workspace.yaml is fixed and deps install cleanly.
 *
 * Auth: this route is INTENTIONALLY public. Contribution surfaces at
 * /submit etc. remain sign-in-gated.
 */

declare global {
  interface Window {
    L?: any;
  }
}

// ImpairmentDoc moved to src/lib/water-status.ts (shared with the
// lazily-loaded LegendBody) — shape unchanged.

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .split(/\s+/)
    .map((w) => (w.length <= 2 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

/**
 * ATTAINS cause names for the impairment banner. titleCase alone
 * mangles the scientific/acronym causes ("Escherichia Coli (e. Coli)",
 * "Pcbs in Fish Tissue") — display exceptions render those the way a
 * reader expects; everything else keeps the plain title-casing.
 */
function displayCause(cause: string): string {
  const c = cause.trim().toLowerCase();
  if (c.includes('escherichia') || /\be\.?\s?coli\b/.test(c)) return 'E. coli';
  if (c.includes('pcb')) return c.includes('fish') ? 'PCBs in fish tissue' : 'PCBs';
  if (c.includes('dissolved oxygen')) return 'low dissolved oxygen';
  return titleCase(cause);
}

// Water-quality status (the map's primary color language) lives in
// src/lib/water-status.ts — shared with the lazily-loaded LegendBody so
// the map fill, the pill, and the legend can never disagree.

// stateFullName / SOURCE_STYLE / sourceKey / rendersOnMap moved to
// src/lib/obs-popup.ts + src/lib/source-style.ts (shared with the
// MapLibre tile-mode path) — behavior unchanged.

export function Explore() {
  const mapEl = useRef<HTMLDivElement | null>(null);
  const [observations, setObservations] = useState<TrailObservationCollection | null>(null);
  const [watershed, setWatershed] = useState<any>(null);
  const [stations, setStations] = useState<any>(null);
  const [ctx, setCtx] = useState<WatershedContextDoc | null>(null);
  // Mirror of `ctx` for the imperative Leaflet closures (bound once per
  // map instance). ctx changes on every cross-boundary pan (dynamic
  // pill), so it must NOT be an init-effect dep — that would tear the
  // map down mid-drag now that the effect has a real cleanup. Reading
  // through the ref also un-stales the pan handler, which previously
  // compared against whatever ctx existed when the map was created.
  const ctxRef = useRef<WatershedContextDoc | null>(null);
  ctxRef.current = ctx;
  const [impairment, setImpairment] = useState<ImpairmentDoc | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Manifest version pinned to whichever data version this client is
  // currently rendering. The background poll detects a bump and triggers
  // an observations re-fetch + atomic state swap.
  const [manifestVersion, setManifestVersion] = useState<string | null>(null);
  // ─── Tile mode (national-scale contract) ─────────────────────────────
  // Presence of a valid manifest `tiles` block flips /explore onto the
  // MapLibre + PMTiles path (lazy chunk); absence keeps the legacy
  // Leaflet + GeoJSON path byte-for-byte intact. See lib/tile-mode.ts.
  const [tileConfig, setTileConfig] = useState<TrailTilesConfig | null>(null);
  // ─── Open-data commons (issue #72) ───────────────────────────────────
  // Presence of a valid manifest `commons` block renders the Sources
  // sheet's "Open data" download row; absence hides it (safe to ship
  // before the first publish). See lib/commons-mode.ts.
  const [commonsConfig, setCommonsConfig] = useState<TrailCommonsConfig | null>(null);
  // Center hit-test result from the tile map (pill population).
  const [tileActive, setTileActive] = useState<ActiveWatershedInfo | null>(null);
  /**
   * Park under the map centre. Drives the "You're in X" chip — the
   * containment answer, which is the part of the places surface that
   * works without hover and therefore the part that works on a phone.
   */
  const [activePark, setActivePark] = useState<ActiveParkInfo | null>(null);

  /**
   * "Show my location".
   *
   * ON-DEVICE ONLY: the fix goes to a GeoJSON source in this tab and
   * nowhere else — not uploaded, not stored, gone on reload. A GPS
   * TRACE is the sensitive class path-privacy-spec.md guards; a
   * self-visible dot is not one, and keeping it that way is what makes
   * this button safe to ship without a consent flow.
   *
   * watchPosition, not getCurrentPosition: someone walking a park needs
   * the dot to keep up. Cleared on toggle-off and on unmount.
   */
  const [locateState, setLocateState] = useState<LocateState>('off');
  const watchIdRef = useRef<number | null>(null);

  function stopWatching() {
    if (watchIdRef.current !== null) {
      navigator.geolocation?.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    tileHandleRef.current?.setUserLocation(null);
  }

  useEffect(() => stopWatching, []);

  function toggleLocate() {
    if (locateState === 'on' || locateState === 'locating') {
      stopWatching();
      setLocateState('off');
      return;
    }
    if (!navigator.geolocation) {
      setLocateState('unavailable');
      return;
    }
    setLocateState('locating');
    let centred = false;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        setLocateState('on');
        tileHandleRef.current?.setUserLocation({
          lat: latitude,
          lon: longitude,
          accuracyM: Number.isFinite(accuracy) ? accuracy : undefined,
        });
        // Recentre ONCE. Re-centring on every update would fight the
        // user for control of the camera every few seconds.
        if (!centred) {
          centred = true;
          const m = tileHandleRef.current?.map;
          if (m) {
            m.easeTo({
              center: [longitude, latitude],
              zoom: Math.max(m.getZoom(), 14),
              duration: 800,
            });
          }
        }
      },
      (err) => {
        stopWatching();
        // Denial is a normal path, not an error state to nag about —
        // same posture the mission surface takes.
        setLocateState(err.code === err.PERMISSION_DENIED ? 'denied' : 'unavailable');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  }
  // Rendered-population stats from the tile map ("{n} in view" + legend).
  // `rung` (zoom ladder, issue #87): 'ladder' below z12 — rollup-based
  // counts, no individual pins by design.
  const [tileStats, setTileStats] = useState<{
    visibleCount: number;
    sources: string[];
    rung?: 'ladder' | 'observations';
  }>({ visibleCount: 0, sources: [] });
  // Readout-bar stats from the tile map controller (zoom ladder). The
  // bar component itself is lazily imported alongside the map chunk so
  // the 40 KB entry gate never pays for it.
  const [tileReadout, setTileReadout] = useState<ReadoutStats | null>(null);
  const [ReadoutBarLazy, setReadoutBarLazy] =
    useState<typeof ReadoutBarComponent | null>(null);
  const [ParkChipLazy, setParkChipLazy] =
    useState<typeof ParkChipComponent | null>(null);
  // Tile-mode minimal water context (issue #78): huc12 → impairment doc
  // from the one-shot versioned /watersheds fetch — the SAME payload the
  // legacy path inlines. Loaded alongside the tile map (lib/tile-
  // impairment.ts) and feeding the banner/status chip here plus the
  // pin-popup badge inside the map controller. Null until tile mode
  // loads it; legacy mode never sets it.
  const [tileImpairmentByHuc, setTileImpairmentByHuc] =
    useState<ReadonlyMap<string, ImpairmentDoc> | null>(null);

  // ─── Firefly filters (tile mode) ─────────────────────────────────────
  // CALENDAR date range + two INDEPENDENT layer toggles, owned here and
  // pushed into the LIVE map controller imperatively.
  //
  // THE TRAP THIS AVOIDS: none of these four values may ever become a dep
  // of the tile-map init effect below, and none of them may key `mapKey`.
  // The pre-firefly filter mechanism (the verified toggle, the legacy
  // time window) worked by TEARING THE MAP DOWN and re-initialising it —
  // acceptable for a once-a-session checkbox, catastrophic for a date
  // input, which would drop the camera, the open popup and every loaded
  // tile on each keystroke. `lit` is baked per-instance CPU-side, so a
  // range change is one buffer re-upload on a running map:
  // handle.setFireflyFilter() below, driven by its own tiny effect.
  const [fromIso, setFromIso] = useState('');
  const [toIso, setToIso] = useState('');
  const [showLitterLayer, setShowLitterLayer] = useState(true);
  // Cleanups default OFF, per the design AND because the tile schema
  // carries no per-scene cleanup history yet — see the empty state in
  // MapTimeControls rather than a silently blank green layer.
  // ?demo: the synthetic scene is rich in cleanups and the green half of
  // the palette is half the point — default the layer on there.
  const [showCleanupLayer, setShowCleanupLayer] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).has('demo');
    } catch {
      return false;
    }
  });
  const [fireflyStats, setFireflyStats] = useState<FireflyStats | null>(null);
  const [MapTimeControlsLazy, setMapTimeControlsLazy] =
    useState<typeof MapTimeControlsComponent | null>(null);
  // Live handle for the imperative filter push. Set by the init effect,
  // cleared by its cleanup.
  const tileHandleRef = useRef<TileExploreMapHandle | null>(null);
  // Legacy-mode Leaflet instance, for the imperative consumers that need
  // the LIVE map in either mode (mission "Show on map" pan + the
  // sheet-open center snapshot). Set by init(), cleared by disposeMap.
  const legacyMapRef = useRef<any>(null);
  // The controller is created ONCE per map instance and must see the
  // filter values current at that moment — read through a ref so the
  // init effect never has to list them as deps.
  const fireflyFilterRef = useRef({
    fromIso: '',
    toIso: '',
    showLitter: true,
    showCleanups: false,
  });
  fireflyFilterRef.current = {
    fromIso,
    toIso,
    showLitter: showLitterLayer,
    showCleanups: showCleanupLayer,
  };
  // Seed the pickers from the data's own bounds until the user first
  // edits one, then never again — from that point the user owns the
  // range and re-seeding would fight their input.
  //
  // NOT a one-shot. Seeding on the FIRST emission made the range
  // order-dependent: whichever features happened to arrive first fixed
  // the calendar forever, and the tiny /fresh JSON normally resolves
  // before the PMTiles byte-range reads. Since fresh rows are BY
  // DEFINITION the newest data, that seeded the range to a single day
  // and embered almost everything before the user touched anything. The
  // controller's minDay/maxDay only ever widen, so re-seeding while
  // untouched can only widen the window too.
  const rangeSeededRef = useRef(false);

  // ─── Timeline transport (PROTOTYPE) ──────────────────────────────────
  // The playhead is a uniform on the firefly layer (setFireflyDay): the
  // rAF loop below never touches buffers or React state per frame — the
  // date readout is throttled to ~6 Hz, the shader does the rest.
  const [timelinePlayheadIso, setTimelinePlayheadIso] = useState<string | null>(null);
  const [timelinePlaying, setTimelinePlaying] = useState(false);
  const [timelineSpeed, setTimelineSpeed] = useState(30);
  /** Fractional playhead day — the animation's own clock, not state. */
  const timelineDayRef = useRef<number | null>(null);
  const TL_DAY_MS = 86_400_000;
  /** Onset flash spans ~0.35 s of wall clock at the current speed. */
  const tlFlashDays = (speed: number) => Math.max(speed * 0.35, 0.5);

  useEffect(() => {
    if (!timelinePlaying) return;
    const fromMs = Date.parse(fromIso || (fireflyStats?.minIso ?? ''));
    const toMs = Date.parse(toIso || (fireflyStats?.maxIso ?? ''));
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
      setTimelinePlaying(false);
      return;
    }
    const fromDay = fromMs / TL_DAY_MS;
    const toDay = toMs / TL_DAY_MS;
    if (timelineDayRef.current === null || timelineDayRef.current >= toDay - 0.5) {
      timelineDayRef.current = fromDay;
    }
    let raf = 0;
    let last: number | null = null;
    let lastUiPush = 0;
    const pushIso = (d: number) =>
      setTimelinePlayheadIso(new Date(Math.floor(d) * TL_DAY_MS).toISOString().slice(0, 10));
    const tick = (ts: number) => {
      if (last !== null) {
        const d = (timelineDayRef.current ?? fromDay) + ((ts - last) / 1000) * timelineSpeed;
        if (d >= toDay) {
          // End of the window: park and stop. No loop — a loop restart
          // relights the whole field at once (the one WCAG-risky frame).
          timelineDayRef.current = toDay;
          tileHandleRef.current?.setFireflyDay(toDay, tlFlashDays(timelineSpeed));
          pushIso(toDay);
          setTimelinePlaying(false);
          return;
        }
        timelineDayRef.current = d;
        tileHandleRef.current?.setFireflyDay(d, tlFlashDays(timelineSpeed));
        if (ts - lastUiPush > 150) {
          lastUiPush = ts;
          pushIso(d);
        }
      }
      last = ts;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // fireflyStats deliberately absent: it changes on every pan and the
    // bounds fallback only matters when the calendar is unbounded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelinePlaying, timelineSpeed, fromIso, toIso]);

  function onTimelineScrub(iso: string): void {
    setTimelinePlaying(false);
    setTimelinePlayheadIso(iso);
    const d = Date.parse(iso) / TL_DAY_MS;
    if (Number.isFinite(d)) {
      timelineDayRef.current = d;
      tileHandleRef.current?.setFireflyDay(d, tlFlashDays(timelineSpeed));
    }
  }

  function onTimelineOff(): void {
    setTimelinePlaying(false);
    setTimelinePlayheadIso(null);
    timelineDayRef.current = null;
    tileHandleRef.current?.setFireflyDay(null);
  }

  // Q4: include unverified observations in pins + aggregate counts.
  // Toggled in the Legend modal; persisted via localStorage.
  const [showUnverified, setShowUnverified] = useState<boolean>(() => readShowUnverified());
  function onToggleShowUnverified(next: boolean) {
    setShowUnverified(next);
    writeShowUnverified(next);
  }

  // Time-window control (trail #temporal-insight): "All time" (default,
  // never written to the URL) / "Past year" / "Past 90 days". Feeds the
  // SAME rendersOnMap predicate as showUnverified, so it re-inits the
  // Leaflet map exactly like the verified toggle (see the init effect's
  // dep array) — one mechanism, no separate re-render path to drift out
  // of sync with the pill/popup/spotlight.
  const [timeWindow, setTimeWindow] = useState<TimeWindow>(
    () => parseMapStateFromUrl().window ?? 'all',
  );
  function onChangeTimeWindow(next: TimeWindow) {
    setTimeWindow(next);
    writeMapStateToUrl({ window: next });
  }

  // Reciprocity (V1 contract §8): the contributor's own recent submission
  // ids (<24h, from localStorage). Pins with these ids ALWAYS render —
  // they bypass the verified-only filter — with a subtle pulse and a
  // "Your report — pending verification" popup line. Read once per mount;
  // stable for the component lifetime.
  //
  // The store keeps raw submission ULIDs, but the backend keys the map
  // feature as `sub-<ulid>` — the set holds every candidate form
  // (observationIdsFor) so `myRecentIds.has(feature.id)` matches the
  // pins as actually served.
  const [myRecentIds] = useState<Set<string>>(
    () => new Set(readRecentSubmissions().flatMap((e) => observationIdsFor(e.id))),
  );
  // The subtractive twin (App Store Guideline 1.2b): pins this device
  // has reported. Quarantine is instant everywhere the server owns —
  // DynamoDB pin, detail endpoint, label queue, public export — but leaf
  // geometry comes from tiles baked by hand, so without this the
  // reporter is told the content is gone and then keeps seeing the dot.
  // Read once per mount for the same reason as myRecentIds: filing a
  // report navigates to /report/:id, which unmounts this page.
  const [reportedIds] = useState<Set<string>>(() => readReportedPinIds());
  // One-time "your report is on the map" toast. Set when a not-yet-seen
  // recent submission id shows up in the loaded observations.
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!observations) return;
    const unseen = readRecentSubmissions().filter((e) => !e.seenOnMap);
    if (unseen.length === 0) return;
    const present = new Set(observations.features.map((f) => String(f.id)));
    const newOnMap = unseen
      .map((e) => e.id)
      .filter((id) => observationIdsFor(id).some((cand) => present.has(cand)));
    if (newOnMap.length === 0) return;
    markSeenOnMap(newOnMap);
    setToast(
      newOnMap.length === 1
        ? 'Your report is on the map — pending verification.'
        : `${newOnMap.length} of your reports are on the map — pending verification.`,
    );
    const t = window.setTimeout(() => setToast(null), 6000);
    return () => window.clearTimeout(t);
  }, [observations]);

  // `?debug` reveals dev-only provenance metadata (ingestion timestamps,
  // source-attempt breakdown) in the footer. Hidden by default in prod.
  const debugMode =
    typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debug');

  // Mirror the same breakpoint the Leaflet legend uses (< 480px = phone).
  // Drives collapse-by-default behavior on the data-sources footer.
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 480;

  // When the initial load eagerly jumped ahead to a just-submitted
  // manifest version, this holds the (older) version the cached manifest
  // endpoint was still serving — the poll ignores it instead of
  // downgrading. See the initial-load effect.
  const staleVersionRef = useRef<string | null>(null);

  // Which of the map overlay sheets is currently open. Tapping a corner
  // button (LegendButton / SourcesButton) sets this; tapping the
  // backdrop or close button resets to null. 'intro' is the one-time
  // first-visit orientation sheet (also reachable from the Sources
  // sheet's "About this map" row and forceable via ?intro=1).
  const [openSheet, setOpenSheet] = useState<
    'legend' | 'sources' | 'intro' | 'missions' | null
  >(null);

  // ─── "Start here" mission surface (issue #94) ────────────────────────
  // Map-center snapshot taken at sheet-open time — the geolocation
  // FALLBACK origin. Read from the LIVE map instance (either mode), not
  // the debounced URL write, so a pan immediately before opening counts.
  const [missionsCenter, setMissionsCenter] = useState<{ lat: number; lon: number } | null>(null);
  /**
   * Park points snapshotted at sheet-open time, same discipline as
   * missionsCenter: read from the LIVE map so a pan immediately before
   * opening counts. null (not []) when this map has no places layer —
   * the sheet renders a DIFFERENT message for "we haven't mapped here"
   * than for "we looked and found none".
   */
  const [missionsParks, setMissionsParks] = useState<ParkFeatureLike[] | null>(null);
  function currentParkPoints(): ParkFeatureLike[] | null {
    const h = tileHandleRef.current;
    if (!h || !h.hasPlacesLayer) return null;
    return h.queryParkPoints() as ParkFeatureLike[];
  }
  function currentMapCenter(): { lat: number; lon: number } | null {
    const tm = tileHandleRef.current?.map;
    if (tm) {
      const c = tm.getCenter();
      return { lat: c.lat, lon: c.lng };
    }
    const lm = legacyMapRef.current;
    if (lm) {
      const c = lm.getCenter();
      return { lat: c.lat, lon: c.lng };
    }
    const s = parseMapStateFromUrl();
    return typeof s.lat === 'number' && typeof s.lon === 'number'
      ? { lat: s.lat, lon: s.lon }
      : null;
  }
  function openMissions() {
    setMissionsCenter(currentMapCenter());
    setMissionsParks(currentParkPoints());
    setOpenSheet('missions');
  }
  // "Show on map": close the sheet and pan the LIVE map (either mode) to
  // the mission target. z12 floor = watershed scale; never zooms OUT.
  function showMissionOnMap(target: { lat: number; lon: number }) {
    setOpenSheet(null);
    const tm = tileHandleRef.current?.map;
    if (tm) {
      tm.easeTo({
        center: [target.lon, target.lat],
        zoom: Math.max(tm.getZoom(), 12),
      });
      return;
    }
    const lm = legacyMapRef.current;
    if (lm) lm.setView([target.lat, target.lon], Math.max(lm.getZoom(), 12));
  }

  // First-visit intro: auto-open ONCE, ~800ms after the first manifest
  // load so the map paints behind the sheet. Gated on the
  // trail.intro_seen localStorage flag AND !navigator.webdriver (keeps
  // it out of e2e/screenshot flows); ?intro=1 forces it for QA. Deep
  // links (?huc=/?z=) still get the intro — shared-link recipients are
  // exactly the unfamiliar audience — with the shared viewport visible
  // behind the sheet.
  const introScheduledRef = useRef(false);
  const introTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!manifestVersion || introScheduledRef.current) return;
    introScheduledRef.current = true;
    const open = shouldAutoOpenIntro({
      search: window.location.search,
      webdriver: navigator.webdriver === true,
      seen: readIntroSeen(),
    });
    if (!open) return;
    introTimerRef.current = window.setTimeout(
      () => setOpenSheet('intro'),
      INTRO_AUTO_OPEN_DELAY_MS,
    );
  }, [manifestVersion]);
  useEffect(
    () => () => {
      if (introTimerRef.current !== undefined) window.clearTimeout(introTimerRef.current);
    },
    [],
  );

  // ANY dismissal of the intro (backdrop, ✕, Esc, primary button)
  // writes the one-time flag.
  function dismissIntro() {
    writeIntroSeen();
    setOpenSheet(null);
  }

  // sourcesInData = the set of source-keys present in the loaded
  // observations geojson. The Legend body uses this to dim rows whose
  // source has zero pins on the current map ("Reddit (no data here)").
  // Computed in the React body so the Legend modal can read it without
  // re-running Leaflet's useEffect; falls back to an empty set before
  // observations load.
  const sourcesInData = useMemo(() => {
    // Ladder rungs (z<12 on ladder tiles): no individual pins render BY
    // DESIGN (rollup mounds instead), so per-source dimming would
    // falsely gray every legend row — treat all sources as present.
    if (tileConfig && tileStats.rung === 'ladder') {
      return new Set<string>(Object.keys(SOURCE_STYLE));
    }
    // Tile mode: the map controller reports the source keys present in
    // the current viewport's rendered features.
    if (tileConfig) return new Set<string>(tileStats.sources);
    if (!observations) return new Set<string>();
    return new Set<string>(
      observations.features.map((f) => sourceKey(f.properties as unknown as { source: { type: string; source_name?: string } })),
    );
  }, [observations, tileConfig, tileStats]);

  // Initial load: fetch manifest → fetch watersheds + observations under
  // that version. The observation feature shape matches the legacy
  // static-file shape (backend reconstructs via JSONB archive + column
  // overlay) so no downstream renderer changes are needed.
  //
  // wq_stations endpoint isn't built yet — `stations` stays as an empty
  // FeatureCollection until that fast-follow ships. Map renders without
  // the cyan dots in the interim.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const manifest = await fetchTrailManifest();
        if (cancelled) return;
        // Open-data row gate — set BEFORE the tile-mode early return so
        // the Sources sheet advertises the commons in both map modes.
        setCommonsConfig(commonsConfigOf(manifest));
        // Tile mode: NO bulk GeoJSON fetches — the map streams PMTiles
        // per viewport instead (contract: "no full-dataset fetches
        // anywhere in tile mode"). The just-submitted eager path is
        // also skipped: reciprocity in tile mode renders the
        // contributor's OWN pins from localStorage stubs (own-pins.ts)
        // and everyone else's via the version-keyed fresh overlay
        // fetched per map init below (issue #83, fresh-overlay.ts) —
        // a fresh submission reaches other clients within one manifest
        // poll cycle, and the next bake absorbs it into the tiles.
        const tiles = tilesConfigOf(manifest);
        if (tiles) {
          setManifestVersion(manifest.version);
          setTileConfig(tiles);
          return;
        }
        // Contract §8: the submit flow stashes the manifest_version the
        // backend returned on 201 (sessionStorage `trail.just_submitted_
        // version`). The /api/trail/manifest response is edge-cached
        // (max-age=5 + SWR) so the fetch above can be a tick stale right
        // after a submission — when the stashed version differs, load THAT
        // version's immutable data immediately instead of waiting for a
        // poll to observe the bump. Consume-once; fall back to the fetched
        // manifest if the newer version's data isn't servable yet.
        const justSubmitted = consumeJustSubmittedVersion();
        if (justSubmitted && justSubmitted !== manifest.version) {
          try {
            const urlStateEager = parseMapStateFromUrl();
            const eager = { ...manifest, version: justSubmitted };
            const loaded = await loadVersion(eager, {
              preferHuc12: urlStateEager.huc12 ?? undefined,
            });
            if (cancelled) return;
            // The edge-cached manifest may keep serving the pre-submit
            // version for a few seconds; don't let the poll "downgrade"
            // us back to it (which would remount the map).
            staleVersionRef.current = manifest.version;
            setManifestVersion(justSubmitted);
            setObservations(loaded.observations);
            setWatershed(loaded.watershedFC);
            setStations({ type: 'FeatureCollection', features: [] });
            setCtx(loaded.ctxDoc);
            setImpairment(loaded.impairmentDoc);
            return;
          } catch {
            // Newer version not fetchable (rare) — continue with the
            // manifest the API actually served.
          }
        }
        // Honor ?huc= for the primary-watershed selection so deep links
        // land on the right ctx (drinking-water banner + Sources sheet).
        const urlState = parseMapStateFromUrl();
        const { observations: obs, watershedFC, ctxDoc, impairmentDoc } =
          await loadVersion(manifest, { preferHuc12: urlState.huc12 ?? undefined });
        if (cancelled) return;
        setManifestVersion(manifest.version);
        setObservations(obs);
        setWatershed(watershedFC);
        setStations({ type: 'FeatureCollection', features: [] });
        setCtx(ctxDoc);
        setImpairment(impairmentDoc);
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Background manifest poll. Detects ingest runs + new submissions and
  // re-fetches observations atomically. The version-string change is
  // the trigger; CloudFront caches each version's data URLs immutably,
  // so the re-fetch normally hits the edge in 30-50ms.
  //
  // Polls faster when the tab has focus (post-submission visibility
  // matters most when the user is looking); slower when hidden so we
  // don't burn API quota for background tabs.
  useEffect(() => {
    if (!manifestVersion) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const latest = await fetchTrailManifest();
        if (cancelled || latest.version === manifestVersion) return;
        // Edge cache still serving the pre-submit version after an eager
        // just-submitted load — not a real bump, skip.
        if (latest.version === staleVersionRef.current) return;
        // Commons pointer rides every manifest bump (a publish bumps the
        // version, so this is the cycle where the row appears/updates).
        setCommonsConfig(commonsConfigOf(latest));
        // Tile mode: a version bump means new tile URLs — swap the
        // config (remounts the tile map) and skip the GeoJSON reload.
        // A manifest that LOSES its tiles block falls back to legacy.
        const latestTiles = tilesConfigOf(latest);
        if (latestTiles) {
          setTileConfig(latestTiles);
          setManifestVersion(latest.version);
          return;
        }
        if (tileConfig) setTileConfig(null);
        // Re-honor ?huc= on every reload so the user's chosen ctx
        // persists across version bumps too.
        const urlState = parseMapStateFromUrl();
        const { observations: obs, watershedFC, ctxDoc, impairmentDoc } =
          await loadVersion(latest, { preferHuc12: urlState.huc12 ?? undefined });
        if (cancelled) return;
        // Atomic swap — all four state setters are batched within one
        // render cycle so the map doesn't flash a stale-mix state.
        setObservations(obs);
        setWatershed(watershedFC);
        setCtx(ctxDoc);
        setImpairment(impairmentDoc);
        setManifestVersion(latest.version);
      } catch (e) {
        // Poll failures shouldn't surface to the user — next tick retries.
        // eslint-disable-next-line no-console
        console.warn('trail manifest poll failed', e);
      }
    };

    let intervalId: number | undefined;
    const startPoll = (delayMs: number) => {
      if (intervalId !== undefined) window.clearInterval(intervalId);
      intervalId = window.setInterval(poll, delayMs);
    };
    startPoll(MANIFEST_POLL_INTERVAL_FOCUSED_MS);

    const onVisibilityChange = () => {
      const hidden = document.visibilityState === 'hidden';
      startPoll(hidden ? MANIFEST_POLL_INTERVAL_HIDDEN_MS : MANIFEST_POLL_INTERVAL_FOCUSED_MS);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      if (intervalId !== undefined) window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [manifestVersion, tileConfig]);

  // ?huc= deep-link seed for the tile-mode pill: used until the first
  // center hit-test emission, then retired (the map center rules).
  const urlHucSeedRef = useRef<string | null>(
    typeof window !== 'undefined' ? parseMapStateFromUrl().huc12 ?? null : null,
  );

  // ─── Tile-map identity (issue #83 delivery, hard requirement 2) ──────
  // A manifest version bump for a NEW SUBMISSION does not change a single
  // tile URL — the URLs are immutable per bake and the new rows ride the
  // /fresh overlay. But `tileConfig` is a fresh object on every poll, so
  // depending on it re-created the entire MapLibre instance (new map, new
  // canvas, new container, both PMTiles archives re-read, any open popup
  // silently destroyed) for what should be one gl.bufferSubData. Depend
  // on the CONTENT instead: this string changes only for a real re-bake.
  const tileKey = tileConfig
    ? [
        tileConfig.observations_url,
        tileConfig.watersheds_url,
        tileConfig.basins_url ?? '',
        tileConfig.schema_version ?? '',
      ].join('|')
    : '';
  // Latest manifest version, for effects that must NOT list it as a dep.
  const manifestVersionRef = useRef<string | null>(null);
  manifestVersionRef.current = manifestVersion;
  // Most recent payloads of the two version-keyed side fetches, so a map
  // created AFTER one resolved still gets it (they race the map init by
  // design — issue #87 cause 3 keeps both OFF the critical path).
  const freshFeaturesRef = useRef<readonly AdaptedObservationFeature[]>([]);
  const impairmentRef = useRef<ReadonlyMap<string, ImpairmentDoc> | null>(null);

  // ─── Version-keyed side data — owned by the VERSION, not the map ─────
  // /fresh (issue #83) and /watersheds (issue #78) are both immutable per
  // manifest version, and both are hydrated into a RUNNING controller.
  // Fetching them here rather than inside the map-init effect is what
  // lets a version bump deliver new data without the map lifecycle being
  // involved at all: hydrateFresh → appendFireflies → gl.bufferSubData.
  // Runs on first load AND on every bump; the map-init effect replays
  // whichever payload has already landed.
  useEffect(() => {
    if (!tileConfig || !manifestVersion) return;
    let cancelled = false;
    import('../lib/fresh-overlay')
      .then((m) => m.loadFreshFeatures(manifestVersion))
      .catch(() => [] as AdaptedObservationFeature[])
      .then(async (f) => {
        if (cancelled) return;
        let all: readonly AdaptedObservationFeature[] = f;
        // ?demo — synthetic litter/cleanup scene for palette evaluation,
        // riding the fresh channel so it survives viewport refreshes
        // (mergeFreshIntoLeaves) like any fresh row. Lazy import keeps it
        // out of the entry chunk for everyone else.
        try {
          const demo = await import('../lib/demo-data');
          if (demo.isDemoDataEnabled()) {
            all = [...f, ...demo.demoFeatures()];
            // Seed the calendar to the demo's own window so the timeline
            // tours exactly the synthetic year (auto-seed would widen it
            // to whatever real data is in view).
            if (!rangeSeededRef.current) {
              rangeSeededRef.current = true;
              setFromIso(demo.DEMO_FROM_ISO);
              setToIso(demo.DEMO_TO_ISO);
            }
          }
        } catch {
          /* demo module is best-effort; the map must not care */
        }
        if (cancelled) return;
        freshFeaturesRef.current = all;
        tileHandleRef.current?.hydrateFresh(all);
      });
    import('../lib/tile-impairment')
      .then((m) => m.loadImpairmentByHuc(manifestVersion))
      .catch(() => new Map<string, ImpairmentDoc>() as ReadonlyMap<string, ImpairmentDoc>)
      .then((m) => {
        if (cancelled) return;
        impairmentRef.current = m;
        setTileImpairmentByHuc(m);
        tileHandleRef.current?.hydrateImpairment(m);
      });
    return () => {
      cancelled = true;
    };
    // tileKey (not tileConfig): a re-bake re-creates the map, and both
    // payloads must be re-applied to the new instance.
  }, [manifestVersion, tileKey]);

  // ─── Tile-mode map lifecycle ─────────────────────────────────────────
  // Lazy-imports the MapLibre controller (maplibre-gl + pmtiles live in
  // the lazy "maplibre" chunk — the 40 KB entry gate never pays for
  // them). Re-runs ONLY when the tile URLs actually change (a re-bake)
  // or on the verified toggle. A manifest version bump alone must NOT
  // reach this effect: see tileKey.
  useEffect(() => {
    // The map itself no longer needs the version — its tile URLs come
    // from tileConfig and both version-keyed payloads are hydrated by the
    // effect above. This only gates creation until the manifest is in.
    if (!tileConfig || !manifestVersionRef.current || !mapEl.current) return;
    let cancelled = false;
    let handle: TileExploreMapHandle | null = null;
    // Cheap CDN warm (issue #87): fire the pmtiles header reads BEFORE
    // any await so the CDN round-trip overlaps the engine-chunk
    // download. Fire-and-forget.
    warmTileHeaders(tileConfig);
    (async () => {
      try {
        // Issue #87 cause 3 is unchanged: the map waits for NOTHING but
        // the engine chunk. Neither /watersheds nor /fresh is fetched
        // here any more — both belong to the manifest VERSION rather than
        // to this map instance, so they have their own effect above and
        // are hydrated into whatever controller is live. All this effect
        // does with them is replay whatever has already landed into a
        // NEWLY created map (see below), which is also what makes the
        // race between the two harmless in either order.
        import('../components/ParkChip')
          .then((m) => {
            if (!cancelled) setParkChipLazy(() => m.default);
          })
          .catch(() => {});
        import('../components/ReadoutBar')
          .then((m) => {
            if (!cancelled) setReadoutBarLazy(() => m.default);
          })
          .catch(() => {});
        // The calendar/toggle strip rides its own lazy chunk. It is
        // inline-styled and imports nothing from firefly-data, so it can
        // never drag the buffer builder into the entry graph.
        import('../components/MapTimeControls')
          .then((m) => {
            if (!cancelled) setMapTimeControlsLazy(() => m.default);
          })
          .catch(() => {});
        const mod = await import('../lib/explore-map-tiles');
        if (cancelled || !mapEl.current) return;
        handle = await mod.createTileExploreMap(mapEl.current, {
          tiles: tileConfig,
          showUnverified,
          myRecentIds,
          ownPins: readOwnPinStubs(),
          reportedIds,
          urlState: parseMapStateFromUrl(),
          // Read through the ref, NOT from the closure: these four values
          // must never appear in this effect's dep array (see the trap
          // note at their declaration).
          firefly: fireflyFilterRef.current,
          // Hotspot drill-down, leaf half: the "what's here" detail read
          // for `sub-` pins. Version through the REF at call time, not
          // the closure — the endpoint family is keyed by MANIFEST
          // version (like /fresh), and a version bump must not re-create
          // the map just to keep popups on the current URL.
          fetchObservationDetail: (id) => {
            const version = manifestVersionRef.current;
            return version ? fetchObservationDetail(version, id) : Promise.resolve(null);
          },
          // Hotspot drill-down, cluster half: live member lookup over
          // the existing /observations bbox read. include_points=false —
          // the member list needs no `_display.points` halos. The
          // adapter is fresh-overlay's (the /observations and /fresh
          // wire shapes are identical by contract), dynamic-imported so
          // the entry chunk doesn't carry it; /observations rows DO
          // carry a server-built _display block (title etc.), which the
          // fresh adapter zeroes, so it is copied back over for the
          // member rows.
          fetchClusterMembers: async (bbox) => {
            const version = manifestVersionRef.current;
            if (!version) return [];
            const [fc, freshMod] = await Promise.all([
              fetchTrailObservations(version, bbox, { verified: 'all', include_points: false }),
              import('../lib/fresh-overlay'),
            ]);
            const members: AdaptedObservationFeature[] = [];
            for (const raw of fc.features ?? []) {
              const adapted = freshMod.adaptApiFreshFeature(raw);
              if (!adapted) continue;
              const disp = (raw as { properties?: { _display?: unknown } }).properties?._display;
              if (disp && typeof disp === 'object') {
                adapted.properties._display = disp as AdaptedObservationFeature['properties']['_display'];
              }
              members.push(adapted);
            }
            return members;
          },
          onActiveWatershed: (info) => {
            // First real hit-test result (even a null one) retires the
            // ?huc= deep-link seed — from here on the map center rules.
            urlHucSeedRef.current = null;
            setTileActive(info);
          },
          onActivePark: (info) => setActivePark(info),
          onViewportStats: (s) => setTileStats(s),
          onReadout: (s) => setTileReadout(s),
          onFireflyStats: (s) => {
            setFireflyStats(s);
            // Keep the pickers pinned to the full data bounds until the
            // user takes ownership of the range (see rangeSeededRef).
            if (!rangeSeededRef.current && s.minIso && s.maxIso) {
              setFromIso(s.minIso);
              setToIso(s.maxIso);
            }
          },
        });
        tileHandleRef.current = handle;
        // Replay whatever the version-keyed fetches have already
        // delivered: they race the map, and whichever finishes first
        // must not lose.
        if (impairmentRef.current) handle.hydrateImpairment(impairmentRef.current);
        if (freshFeaturesRef.current.length > 0) {
          handle.hydrateFresh(freshFeaturesRef.current);
        }
        // ?demo — stamp deterministic impairment classes onto whatever
        // HUC-12s the tiles deliver, so the four-way watershed wash is
        // demonstrable (deployed tiles carry no impairment_class at
        // all). Real hydrated docs win over demo classes. Listener dies
        // with the map on dispose.
        {
          const demoMod = await import('../lib/demo-data');
          if (demoMod.isDemoDataEnabled() && !cancelled) {
            const liveHandle = handle;
            const classByHuc = new Map<string, import('../lib/water-status').WaterStatus>();
            const demoDocs = new Map<string, ImpairmentDoc>();
            const stamp = () => {
              if (tileHandleRef.current !== liveHandle) return;
              const feats = liveHandle.map.querySourceFeatures('watersheds', {
                sourceLayer: 'watersheds',
              });
              let changed = false;
              for (const f of feats) {
                const huc = String((f.properties as Record<string, unknown>)?.huc12 ?? '');
                if (!huc || classByHuc.has(huc)) continue;
                // Demo classes stay inside the Potomac basin (0207*),
                // where the synthetic scene lives — stamping every
                // loaded HUC painted a fake nationwide assessment.
                if (!huc.startsWith('0207')) continue;
                const real = impairmentRef.current?.get(huc);
                const st = real ? waterStatusOf(real) : demoMod.demoWaterStatusFor(huc);
                classByHuc.set(huc, st);
                if (!real) {
                  const doc = demoMod.demoImpairmentDocFor(huc, st);
                  if (doc) demoDocs.set(huc, doc);
                }
                changed = true;
              }
              if (!changed) return;
              const paint = demoMod.demoWatershedPaint(classByHuc);
              liveHandle.map.setPaintProperty('ws-fill', 'fill-color', paint.fillColor);
              liveHandle.map.setPaintProperty('ws-line', 'line-color', paint.lineColor);
              liveHandle.map.setPaintProperty('ws-line', 'line-dasharray', paint.lineDash);
              liveHandle.map.setPaintProperty('ws-line', 'line-width', paint.lineWidth);
              // Pills/popups/readout see the same classes the paint does;
              // real docs ride along untouched.
              const merged = new Map(demoDocs);
              for (const [k, v] of impairmentRef.current ?? []) merged.set(k, v);
              setTileImpairmentByHuc(merged);
              liveHandle.hydrateImpairment(merged);
            };
            liveHandle.map.on('idle', stamp);
          }
        }
        if (cancelled) {
          handle.dispose();
          handle = null;
          return;
        }
        // Reciprocity toast (tile-mode variant of the observations-based
        // effect below): own-pin stubs render unconditionally, so any
        // not-yet-seen recent submission with a stub is "on the map" now.
        const stubs = new Set(readOwnPinStubs().map((e) => e.id));
        const unseen = readRecentSubmissions().filter(
          (e) => !e.seenOnMap && stubs.has(e.id),
        );
        if (unseen.length > 0) {
          markSeenOnMap(unseen.map((e) => e.id));
          setToast(
            unseen.length === 1
              ? 'Your report is on the map — pending verification.'
              : `${unseen.length} of your reports are on the map — pending verification.`,
          );
          window.setTimeout(() => setToast(null), 6000);
        }
      } catch (e) {
        if (!cancelled) setError(String(e));
      }
    })();
    return () => {
      cancelled = true;
      setTileActive(null);
      setTileReadout(null);
      if (tileHandleRef.current === handle) tileHandleRef.current = null;
      handle?.dispose();
    };
    // DEPS ARE LOAD-BEARING, IN BOTH DIRECTIONS.
    //  - fromIso / toIso / showLitterLayer / showCleanupLayer are absent:
    //    any of them would re-create the whole MapLibre instance on every
    //    date-picker keystroke, dropping the camera, the open popup,
    //    every loaded tile and the GPU buffer. They flow through the
    //    effect below instead.
    //  - manifestVersion is absent, and tileKey stands in for tileConfig:
    //    a version bump for a new submission changes NO tile URL, and
    //    tearing the map down to deliver one point violates the
    //    incremental-delivery requirement. New rows arrive through the
    //    /fresh effect above → hydrateFresh → gl.bufferSubData.
    //  - showUnverified stays: it changes the render predicate for every
    //    feature, and re-init is the existing (pre-firefly) mechanism.
  }, [tileKey, showUnverified]);

  // ─── Firefly filter → LIVE map (never a re-init) ─────────────────────
  // One imperative call into the running controller: it rebuilds the
  // instance buffer from features it ALREADY has and repaints. No
  // network, no camera change, no source or style mutation.
  useEffect(() => {
    tileHandleRef.current?.setFireflyFilter({
      fromIso,
      toIso,
      showLitter: showLitterLayer,
      showCleanups: showCleanupLayer,
    });
  }, [fromIso, toIso, showLitterLayer, showCleanupLayer]);

  // ─── Tile-mode watershed context (pill sentence + Sources sheet) ─────
  // Per-watershed context/impairment keep coming from the existing
  // /api/trail/* endpoints for COVERED HUCs; anywhere else the fetch
  // 404s and the pill gracefully shows tile-derived name + count only.
  const activeTileHuc = tileActive?.huc12 ?? urlHucSeedRef.current;
  useEffect(() => {
    if (!tileConfig || !manifestVersion) return;
    if (!activeTileHuc) {
      setCtx(null);
      setImpairment(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const doc = await fetchTrailWatershedContext(manifestVersion, activeTileHuc);
        if (cancelled) return;
        const wsCtx = doc?.watershed_context as unknown;
        // The endpoint embeds the same WatershedContextDoc the legacy
        // watersheds payload carried; be defensive about partial docs.
        if (
          wsCtx && typeof wsCtx === 'object' &&
          (wsCtx as { drains_to?: unknown }).drains_to
        ) {
          setCtx(wsCtx as WatershedContextDoc);
        } else {
          setCtx(null);
        }
        const imp = doc?.impairment_status as unknown;
        setImpairment(
          imp && typeof imp === 'object' &&
          Array.isArray((imp as { impaired_assessment_units?: unknown }).impaired_assessment_units)
            ? (imp as ImpairmentDoc)
            : null,
        );
      } catch {
        // Uncovered HUC (or transient failure) — graceful absence.
        if (!cancelled) {
          setCtx(null);
          setImpairment(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tileConfig, manifestVersion, activeTileHuc]);

  useEffect(() => {
    if (!mapEl.current || !observations || !watershed || !stations) return;
    // Set by init() once the Leaflet instance exists. The effect cleanup
    // invokes it on dep change (verified toggle, data-version swap) and
    // on unmount, so a re-init never strands the previous map instance.
    let disposeMap: (() => void) | null = null;
    if (!window.L) {
      // Leaflet's CDN tags moved out of index.html (tile mode must not
      // pay for them) — inject them now, then keep polling for window.L
      // exactly as before.
      ensureLeafletLoaded();
      const tick = setInterval(() => {
        if (window.L) {
          clearInterval(tick);
          init();
        }
      }, 50);
      return () => {
        clearInterval(tick);
        disposeMap?.();
      };
    }
    init();
    return () => disposeMap?.();

    function init() {
      const L = window.L!;
      if (!mapEl.current) return;
      if ((mapEl.current as any)._leaflet_id) return;

      const map = L.map(mapEl.current, {
        // Disable the default top-left zoom control; we re-add at top-right
        // below so the watershed/HUC-12 hook can occupy the full top-left
        // without being clipped by the zoom buttons on mobile.
        zoomControl: false,
        attributionControl: true,
        // v1.6: lock the viewport to where we actually have data so the
        // map stops implying "USA-wide coverage" when we only cover the
        // Mid-Atlantic. minZoom keeps users above the world-wrap
        // threshold; maxBounds + viscosity hold them inside the
        // East-Coast box; worldCopyJump off so the map can't tile
        // horizontally.
        // Basin floor: never zoom out past a metro/basin scale. Rendering
        // the whole country at once makes no sense and won't scale — the
        // map is always a bounded basin. Pairs with the fitBounds landing
        // below, which opens framed on the ACTIVE basin's data.
        minZoom: 9,
        maxBounds: [[24, -95], [50, -65]],
        maxBoundsViscosity: 1.0,
        worldCopyJump: false,
      });
      // Test hook: expose the Leaflet map so Playwright can drive zoom
      // programmatically without flaky UI clicks. Namespaced so it doesn't
      // collide with anything else; idempotent on hot-reload.
      (window as any)._dregsbaneTrailMap = map;
      legacyMapRef.current = map;

      // ─── Dedicated pane for observation markers + pile tap-catchers ───
      //
      // Fixes: "zooming in and clicking on mound results in clicking on
      // watershed boundary" — watershed polygons (interactive, incl.
      // their near-invisible hit-test fill) live in Leaflet's default
      // 'overlayPane'. A dedicated pane ABOVE it, applied to every
      // observation circleMarker AND the OLM pile transparent
      // tap-catcher, guarantees a tap that lands on/near a pin or mound
      // always resolves to ITS popup — the watershed only wins on open
      // ground where no marker pane element is painted. (The pile's
      // VISUAL mountain silhouette is a separate absolutely-positioned
      // SVG with pointer-events:none — it never has anything to "block",
      // divIcon or otherwise; only this transparent catcher is
      // clickable.) zIndex 550 sits above overlayPane(400)/shadowPane
      // (500), below tooltipPane(650)/popupPane(700) so popups still
      // draw on top.
      const obsPane = map.createPane('trailObs');
      obsPane.classList.add('trail-obs-pane');
      obsPane.style.zIndex = '550';

      L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 19,
          // v1.6: stop tiles from repeating horizontally at low zoom so
          // the basemap doesn't tile the Americas 3× across the viewport.
          noWrap: true,
        },
      ).addTo(map);

      // ─── Watershed polygons ────────────────────────────────────────────
      //
      // Closes #21. Two coupled fixes:
      //
      // 1. CLICKABLE: tap a polygon → popup with HUC name + observation
      //    count + an instruction to open the Sources sheet for citations.
      //    The previous critique flagged that the polygons dominate
      //    visual hierarchy but had no listener — false affordance.
      //
      // 2. ZOOM-AWARE STYLE: at z ≤ 11 the polygons recede so trash pins
      //    read as figure not noise (the founder's "info and signal is
      //    hard to observe" complaint). At z ≥ 12 they regain prominence
      //    because that's the zoom at which geography starts mattering
      //    to the user.
      //
      // Polygon fill carries EPA water-quality status (the map's primary
      // colour language) instead of a uniform grey. The whole water
      // dimension is COOL now — blue for "nothing on record", violet for
      // impaired — because warm was double-booked between "there is
      // litter here" and "this water is impaired" (measured: protan
      // ΔE 2.2 between the old trash tint and the litter mark).
      //
      // 'good' and 'unknown' share ONE blue and are separated by STROKE
      // STYLE, not hue: WATER_STATUS_STROKE dashes the unassessed. No
      // fourth cool hue survives dichromacy at these lightnesses, and
      // dash costs zero contrast budget. Zoom still modulates prominence:
      // at z≤11 the water layer stays lower so pins read as figure; at
      // z≥12 it strengthens as geography starts to matter to the user.
      function polygonStyleForZoom(z: number, feature?: any) {
        const status = waterStatusOf(
          feature?.properties?.impairment_status as ImpairmentDoc | undefined,
        );
        const color = WATER_STATUS_COLOR[status];
        const quiet = status === 'unknown';
        const hot = status === 'trash';
        // Solid = EPA assessed it. Dashed = nobody has. Mirrors
        // impairmentDashArray() on the tile path.
        const dashArray =
          WATER_STATUS_STROKE[status] === 'dashed' ? '4 4' : undefined;
        const fill =
          status === 'unknown'
            ? (z <= 11 ? 0.04 : 0.06)
            : status === 'trash'
              ? (z <= 11 ? 0.22 : 0.3)
              : status === 'impaired'
                ? (z <= 11 ? 0.14 : 0.2)
                : (z <= 11 ? 0.12 : 0.16); // good
        if (z <= 11) {
          return {
            color, weight: hot ? 1.4 : quiet ? 0.7 : 1.1, opacity: quiet ? 0.35 : 0.7,
            fillColor: color, fillOpacity: fill, dashArray,
          };
        }
        return {
          color, weight: hot ? 2.6 : quiet ? 1.2 : 2, opacity: quiet ? 0.55 : 0.9,
          fillColor: color, fillOpacity: fill, dashArray,
        };
      }

      // The rendered observation population — literally the SAME
      // predicate (rendersOnMap) the marker forEach below early-returns
      // on: verified filter + reciprocity bypass + the OLM tag-data
      // gate + the time-window filter. K in the polygon popup ("showing
      // {K} on the map now") counts against this set, so K can never
      // include a record that draws nothing.
      const renderedFeatures = observations!.features.filter((f) =>
        rendersOnMap(f, showUnverified, myRecentIds, timeWindow),
      );
      // All-time (window='all') rendered population — the verified
      // filter still applies, but the time-window narrowing doesn't.
      // Feeds the year-bar strip (which IS the time context) and the
      // "(+N undated)" pill note (undated records that the CURRENT
      // window is hiding). Never used for K/"showing" — those stay
      // window-scoped.
      const allTimeRenderedFeatures = observations!.features.filter((f) =>
        rendersOnMap(f, showUnverified, myRecentIds, 'all'),
      );

      // huc12 → display name for every loaded polygon (O(1) badge lookups).
      const wsNameByHuc = new Map<string, string>();
      // huc12 → impairment doc, same loop, same O(1) lookup shape (issue
      // #51: this data was already loaded for the top banner + Sources
      // citation drawer — join it down to individual pins too).
      const wsImpairmentByHuc = new Map<string, ImpairmentDoc>();
      for (const wf of (watershed.features ?? []) as Array<{ properties?: any }>) {
        const wp = wf.properties ?? {};
        const h = wp.huc12 ? String(wp.huc12) : '';
        if (h) wsNameByHuc.set(h, String(wp.name ?? h));
        const imp = wp.impairment_status as ImpairmentDoc | undefined;
        if (h && imp) wsImpairmentByHuc.set(h, imp);
      }

      // ─── Region spotlight (CSS-class toggling only) ────────────────────
      //
      // On polygon click the map container gets `trail-spotlight-active`,
      // member pile/circle markers get `trail-spotlight-member`, every
      // other marker `trail-spotlight-dim`, and the clicked polygon
      // `trail-spotlight-boundary`. No marker re-render, no new layers;
      // state clears on popup close. Membership uses the stamped
      // context.huc12 (ray-cast fallback resolved once at load for null
      // stamps) — the identical population behind every count.
      let spotlight: { huc12: string } | null = null;
      let spotlightBoundaryEl: any = null;
      // EVERY observation circle marker (OLM included, now that the pile
      // renderer is gone), with its resolved huc12 (stamped, or local PIP
      // when the stamp was null; null = outside loaded polygons).
      const plainMarkers: Array<{ marker: any; huc12: string | null }> = [];
      function setSpotClasses(el: any, isMember: boolean) {
        if (!el || !el.classList) return;
        el.classList.toggle('trail-spotlight-member', isMember);
        el.classList.toggle('trail-spotlight-dim', !isMember);
      }
      function applySpotlight() {
        if (!spotlight || !mapEl.current) return;
        mapEl.current.classList.add('trail-spotlight-active');
        for (const pm of plainMarkers) {
          setSpotClasses(pm.marker.getElement?.(), pm.huc12 === spotlight.huc12);
        }
      }
      function clearSpotlight() {
        spotlight = null;
        if (mapEl.current) mapEl.current.classList.remove('trail-spotlight-active');
        if (spotlightBoundaryEl) {
          spotlightBoundaryEl.classList?.remove('trail-spotlight-boundary');
          spotlightBoundaryEl = null;
        }
        for (const pm of plainMarkers) {
          const el = pm.marker.getElement?.();
          el?.classList?.remove('trail-spotlight-member', 'trail-spotlight-dim');
        }
      }

      // Straddling clusters (region-count contract §5): if a member OLM
      // cluster's _display.points ([lon, lat]) spill across the clicked
      // boundary into ANOTHER loaded polygon, name it. Counts stay
      // record-unit — the full member-split lands with the PMTiles bake.
      function straddleOtherNameFor(
        huc12: string,
        geometry: any,
        members: Array<{ properties?: any }>,
      ): string | null {
        for (const f of members) {
          const pts = (f.properties as any)?._display?.points;
          if (!Array.isArray(pts) || pts.length < 2) continue;
          for (const pt of pts) {
            if (!Array.isArray(pt) || pt.length !== 2) continue;
            const lonP = Number(pt[0]);
            const latP = Number(pt[1]);
            if (!Number.isFinite(lonP) || !Number.isFinite(latP)) continue;
            if (pointInPolygonGeometry(latP, lonP, geometry)) continue;
            const other = findContainingWatershed(latP, lonP, watershed);
            if (other && other.huc12 !== huc12) {
              return String((other.feature.properties as any).name ?? other.huc12);
            }
          }
        }
        return null;
      }

      // Polygon-click popup content, computed lazily at open time so each
      // click touches ONE polygon × visible features only. The recorded
      // total is properties.obs_count — stamped server-side from the
      // per-watershed counter, counts ALL records regardless of verified.
      // Pre-backfill fallback: the SAME stamped-first membership rule as
      // K (membersOfWatershed) applied to ALL fetched records — so the
      // two numbers in one popup can differ only by the viewer's render
      // filters, never by membership definition.
      function watershedPopupHtml(feature: any, huc12: string, name: string, state: string): string {
        const props = feature.properties ?? {};
        const recordedTotal =
          typeof props.obs_count === 'number'
            ? props.obs_count
            : countRecords(membersOfWatershed(observations!.features, huc12, feature.geometry));
        const members = membersOfWatershed(renderedFeatures, huc12, feature.geometry);
        const copy = buildWatershedPopupCopy({
          recordedTotal,
          showingCount: countRecords(members),
          straddleOtherName: straddleOtherNameFor(huc12, feature.geometry, members),
        });
        // Year-bar strip: verified-filter-respecting but time-window-
        // IGNORING member set (allTimeRenderedFeatures) — the bars ARE
        // the time context the window control narrows away from. Empty
        // watershed → no chart (matches the "be the first" empty state
        // above; an all-zero 8-bar chart would just be noise).
        const yearBarMembers = membersOfWatershed(allTimeRenderedFeatures, huc12, feature.geometry);
        const yearBarHtml = yearBarMembers.length
          ? buildYearBarHtml(computeYearBars(yearBarMembers), pastVsPriorYear(yearBarMembers))
          : '';
        // Shell HTML shared with the tile-mode path (obs-popup.ts) —
        // identical copy in both engines by construction.
        return buildWatershedPopupShellHtml({ name, huc12, state, copy, yearBarHtml });
      }

      // ─── Litter → water flow trace (USGS NLDI) ─────────────────────────
      //
      // Tap any watershed → trace where its water (and litter) drains,
      // toward the drinking-water intake it feeds. The NLDI fetch + draw
      // machinery lives in src/lib/flow-trace.ts, dynamically imported on
      // the FIRST watershed tap so the entry chunk stays under the 40 KB
      // first-paint gate (§10). Ordering is safe by construction: open →
      // show and close → clear both chain off the same singleton import
      // promise (callbacks run in registration order), flowReq drops a
      // show whose popup closed before the chunk arrived, and the
      // handle's own internal token cancels in-flight NLDI fetches.
      let flowTracePromise: Promise<import('../lib/flow-trace').FlowTraceHandle> | null = null;
      const getFlowTrace = () =>
        (flowTracePromise ??= import('../lib/flow-trace').then((m) => m.createFlowTrace(L, map)));
      let flowReq = 0;

      const watershedLayer = L.geoJSON(watershed, {
        style: (feature: any) => polygonStyleForZoom(map.getZoom(), feature),
        onEachFeature: (feature: any, layer: any) => {
          const props = feature.properties ?? {};
          const huc12 = String(props.huc12 ?? '');
          const name = String(props.name ?? '(unnamed watershed)');
          const state = String(props.state ?? '');
          layer.bindPopup(() => watershedPopupHtml(feature, huc12, name, state), {
            maxWidth: 280,
          });
          layer.on('popupopen', () => {
            spotlight = { huc12 };
            const el = layer.getElement?.();
            if (el) {
              el.classList?.add('trail-spotlight-boundary');
              spotlightBoundaryEl = el;
            }
            applySpotlight();
            // Trace the litter→water path from the watershed's center.
            const c = layer.getBounds?.().getCenter?.();
            const drainsTo = props.watershed_context?.drains_to;
            if (c) {
              const my = ++flowReq;
              void getFlowTrace().then((ft) => {
                if (my === flowReq) ft.show(c.lat, c.lng, drainsTo);
              });
            }
          });
          layer.on('popupclose', () => {
            clearSpotlight();
            ++flowReq;
            // Only touch the module if a tap ever requested it — a close
            // must never be what triggers the chunk download.
            if (flowTracePromise) void flowTracePromise.then((ft) => ft.clear());
          });
        },
      }).addTo(map);

      // Restyle on every zoomend so the figure/ground inversion at z≤11
      // releases as the user zooms in. Combines with the URL-state
      // setView below — initial render reads the URL, subsequent zoom
      // events re-style polygons.
      map.on('zoomend', () => {
        watershedLayer.setStyle((f: any) => polygonStyleForZoom(map.getZoom(), f));
      });

      // Initial view: a shareable deep-link (?z=&c=) always wins (closes
      // #20). Otherwise, open framed on the ACTIVE WATER BASIN — the extent
      // of the observations we actually have — rather than a fixed wide
      // view. This is also the national-scale primitive: never render the
      // whole country, always a bounded basin. fitBounds honors the minZoom
      // floor and is capped so a single tight basin doesn't slam to street
      // level.
      {
        const urlState = parseMapStateFromUrl();
        if (urlState.lat != null && urlState.lon != null && urlState.zoom != null) {
          map.setView([urlState.lat, urlState.lon], urlState.zoom);
        } else {
          const pts = (observations?.features ?? [])
            .map((f: any) => f.geometry?.coordinates)
            .filter((c: any) => Array.isArray(c) && Number.isFinite(c[0]) && Number.isFinite(c[1]))
            .map((c: any) => [c[1], c[0]] as [number, number]);
          // Frame the DENSE CORE of the data (the active basin), not the
          // full extent — a far-flung legacy cluster (e.g. Fredericksburg)
          // or sparse fringes otherwise drag the fit out to the whole
          // region. Use the 10–90th percentile box on each axis: robust to
          // outliers on every side, frames where the data actually is.
          let bounds: any = null;
          if (pts.length >= 10) {
            const lat = pts.map((p) => p[0]).sort((a, b) => a - b);
            const lon = pts.map((p) => p[1]).sort((a, b) => a - b);
            const q = (arr: number[], p: number) =>
              arr[Math.min(arr.length - 1, Math.max(0, Math.floor(p * arr.length)))];
            bounds = L.latLngBounds(
              [q(lat, 0.1), q(lon, 0.1)],
              [q(lat, 0.9), q(lon, 0.9)],
            );
          } else if (pts.length) {
            bounds = L.latLngBounds(pts);
          }
          if (bounds && bounds.isValid()) {
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
          } else {
            map.setView(
              [DEFAULT_MAP_LANDING.lat, DEFAULT_MAP_LANDING.lon],
              DEFAULT_MAP_LANDING.zoom,
            );
          }
        }
      }

      // Real WQ monitoring stations. A monitoring station is a place
      // where somebody MEASURES water — an instrument, not the water
      // itself — so it takes the water blue, not a second cyan. (The old
      // #5ec3ff / #3a8fc5 pair was the last non-water blue on the map and
      // the reason "blue = water" could not be stated as a rule.) Size,
      // not hue, still separates stream gauges from other stations.
      stations.features.forEach((f: any) => {
        const [lon, lat] = f.geometry.coordinates;
        const p = f.properties;
        const type = p.MonitoringLocationTypeName ?? 'station';
        const isStream = /stream|river|creek/i.test(type);
        const marker = L.circleMarker([lat, lon], {
          radius: isStream ? 6 : 4,
          color: WATER_BODY_LINE,
          weight: 1,
          fillColor: WATER_BODY_LINE,
          fillOpacity: isStream ? 0.85 : 0.55,
        }).addTo(map);
        marker.bindPopup(`
          <div style="font: 12px/1.4 system-ui, sans-serif; color: var(--tt-popup-ink);">
            <div style="font-weight: 600; color: var(--tt-ink-water);">${escape(p.MonitoringLocationName ?? p.MonitoringLocationIdentifier)}</div>
            <div style="color: var(--tt-popup-ink-muted);">${escape(type)}</div>
            <div style="color: var(--tt-popup-ink-muted);">${escape(p.OrganizationFormalName ?? '')}</div>
            <div style="margin-top: 6px; color: var(--tt-popup-ink-muted); font-size: 11px;">
              Source: EPA Water Quality Portal · ${escape(p.MonitoringLocationIdentifier)}
            </div>
          </div>
        `);
      });

      // SOURCE_STYLE + sourceKey live at module scope so the Legend modal
      // (rendered as Preact JSX, not Leaflet imperative) can reuse them.
      // See definitions just below the Explore() component body.

      // ─── Observation markers ───────────────────────────────────────────
      //
      // THE PAINTERLY PILE RENDERER IS GONE, in both engines. It used to
      // live here: ~800 lines of screen-space hierarchical clustering, an
      // SVG "mountain field" overlay (per-cluster gradients, gouache
      // grain filters, profile-share bands, footholds, ridge connectors),
      // transparent circleMarker tap-catchers and a bbox-diagonal
      // prominence/hysteresis handler — plus TrashPile.ts and
      // hierarchical-clusterer.ts, which it was the only consumer of.
      //
      // Two things killed it. (1) Its marks were sized in GROUND units
      // with a linear zoom factor, so they blobbed at city zoom — the
      // exact failure the firefly renderer is shaped to avoid. (2) It was
      // a THIRD answer to "how much litter is here", alongside the tile
      // rollups and the leaves, and it could disagree with both.
      //
      // This LEGACY (non-tile) path is the rollback surface: it runs only
      // when the manifest carries no `tiles` block. It now draws every
      // observation — OLM included — as one honest circle marker, sized
      // by √items, with the SAME popup/ledger/verb/spotlight wiring every
      // other source already had. Firefly cartography is the tile path
      // (lib/explore-map-tiles.ts); this is deliberately plain rather
      // than a second half-implementation of it.

      // Issue #69: per-observation popup rebuilders for the post-log
      // optimistic ledger update. Keyed by the STABLE observation id —
      // merged screen-space mounds have no stable id, carry no ledger,
      // and never appear here. `props` is the live in-memory feature
      // properties object the rebuild closure reads, so mutating it and
      // calling refresh() re-renders the popup with the new claim.
      const ledgerPopupRefreshers = new Map<
        string,
        { props: Record<string, any>; refresh: () => string }
      >();

      // ─── All observations forEach ──────────────────────────────────────
      observations!.features.forEach((f) => {
        const p = f.properties as any;
        // Reciprocity (contract §8): the contributor's own recent
        // submissions ALWAYS render, verified or not — otherwise their
        // report silently vanishes behind the verified-only default.
        const isMine = myRecentIds.has(String(f.id));
        // ONE render predicate (rendersOnMap): the Q4 verified toggle +
        // the reciprocity bypass + the time window. The same predicate
        // builds renderedFeatures above and visibleFeatures in the
        // component body, so "showing {K}" / "{n} in view" count exactly
        // the records this loop turns into a marker. Public-source pins
        // (Reddit/OLM/news/curated) are inserted with verified=true at
        // ingest; only user_submission pins start unverified.
        //
        // The OLM tag-data gate that used to sit inside this predicate is
        // gone with the pile renderer (source-style.ts): a count-only OLM
        // cluster now DRAWS, so it is also COUNTED. That is a deliberate
        // increase in both numbers, and it keeps them equal.
        if (!rendersOnMap(f, showUnverified, myRecentIds, timeWindow)) return;
        const [lon, lat] = f.geometry.coordinates;
        const d = p._display ?? {};
        // One membership definition everywhere: trust the stamped
        // context.huc12 when present — the backend stamps new records by
        // point-in-polygon at write time and its backfill re-stamps ALL
        // legacy (bbox-era) records geometrically — and ray-cast locally
        // ONLY when the stamp is null. `resolvedHuc` feeds the badge,
        // the spotlight membership, and the pile aria-labels: the
        // identical rule membersOfWatershed applies to K and to the
        // recorded-total fallback.
        const stamped =
          typeof p.context?.huc12 === 'string' && p.context.huc12
            ? (p.context.huc12 as string)
            : null;
        const resolvedHuc =
          stamped ?? findContainingWatershed(lat, lon, watershed)?.huc12 ?? null;
        const wsName = resolvedHuc ? wsNameByHuc.get(resolvedHuc) ?? null : null;
        const wsImpairment = resolvedHuc ? wsImpairmentByHuc.get(resolvedHuc) ?? null : null;
        const inMapped = wsName !== null;
        const distMi = p.context?.distance_miles_from_centroid;
        const bearing = p.context?.distance_bearing;
        const ageDays = Math.floor(
          (Date.now() - new Date(p.observed_at).getTime()) / 86_400_000,
        );
        const ageStr = relativeAgeString(ageDays);
        const style = SOURCE_STYLE[sourceKey(p)];

        const isOlm = sourceKey(p) === 'olm';
        // v1.5: recency-pulse class added to recent non-OLM markers
        // (Reddit/news/curated have real timestamps; OLM is ingestion-time
        // only unless the enrich backfill stamped date_basis
        // 'photo_dates', so the pulse stays off for OLM).
        const isRecent = !isOlm && ageDays < 14 && p.source.type !== 'cv_detection';
        // Recency-as-vividness (trail #temporal-insight): fresh (<6mo)
        // stays full-strength; 6-24mo dims to 0.75 opacity; >24mo dims
        // to 0.5 + a slight desaturation filter. Undated/fabricated-OLM
        // dates get no class (full vividness — no evidence to dim on).
        const vividClass = ageBucketClassName(f);
        // ONE marker per observation, every source. OLM used to branch
        // off into the pile renderer here; it now takes the same path as
        // everything else, with radius carrying the item count that the
        // pile's silhouette used to.
        const itemCount = featureItemCount(f);
        const radius = isOlm
          ? Math.max(7, Math.min(22, 3.2 * Math.sqrt(Math.max(1, itemCount ?? 1))))
          : inMapped
            ? 9
            : 6;
        const pulseClass = isMine ? 'trail-marker--mine' : isRecent ? 'trail-marker--recent' : null;
        const marker: any = L.circleMarker([lat, lon], {
          radius,
          color: style.color,
          weight: 2,
          fillColor: style.color,
          fillOpacity: inMapped ? 0.55 : 0.25,
          // Dedicated pane (see map creation above) so this catcher
          // always wins a tap over the watershed polygon underneath.
          pane: 'trailObs',
          className: [pulseClass, vividClass].filter(Boolean).join(' ') || undefined,
        }).addTo(map);
        // Badge names the containing watershed via the huc12 stamp
        // (fixes the false "adjacent watershed" badge on user pins).
        // The distance line only renders OUTSIDE mapped watersheds and
        // always names its anchor (the ingest centroid, Massaponax
        // Creek). Builder shared with the tile-mode path (obs-popup.ts).
        const wsBadge = buildWsBadgeHtml({ wsName, distMi, bearing, impairment: wsImpairment });
        // Cleanup Ledger P1: anchored to THIS observation's own stable
        // id. With the zoom-dependent merged mounds gone, EVERY marker
        // now has one, so the "zoom in to log a cleanup" dead-end is
        // gone from this path too. The whole popup build lives in a
        // rebuild closure so the post-log optimistic update (issue #69)
        // can re-render it from the mutated in-memory feature; a
        // manifest version bump replaces the entire observations
        // collection, so the server's copy always wins on the next load.
        const obsId = String(f.id ?? `${lat},${lon}`);
        const isDc311 = sourceKey(p) === 'dc311';
        const buildPopupHtml = () => {
          // recent_cleanups / removed_count are absent until the first
          // claim is ever logged against this scene (or while the live
          // Lambda predates the PR #28 read path); the estimator
          // degrades honestly to Tier D ("not enough data yet") rather
          // than fabricate a trend from nothing.
          const recentCleanups: CleanupEvent[] = Array.isArray(p.recent_cleanups)
            ? p.recent_cleanups
            : [];
          // Issue #69: dated same-scene history now feeds the estimator
          // (scene-history.ts) — OLM photo-date accumulation windows
          // fire Tier A; sweep-dated OLM rows count as UNDATED per the
          // time-window.ts honesty rule and still degrade to Tier D.
          const rate = estimateRate(estimatorInputForFeature(f));
          const totalKnownItems = featureItemCount(f);
          const removedCount = typeof p.removed_count === 'number' ? p.removed_count : 0;
          const itemsRemainingEstimate =
            totalKnownItems != null ? Math.max(0, totalKnownItems - removedCount) : null;
          const goalGradient = estimateGoalGradient({ itemsRemainingEstimate, rate });
          const removalLine =
            removedCount > 0
              ? `neighbors removed ~${removedCount.toLocaleString()} item${removedCount === 1 ? '' : 's'} so far`
              : null;
          const ledgerHtml = buildCleanupLedgerHtml(obsId, {
            rate,
            removalLine,
            goalGradient,
            events: recentCleanups,
          });
          // Honesty rules (contract §8): OLM timestamps are only real when
          // the enrich-olm-dates backfill stamped date_basis 'photo_dates'
          // — then the popup shows the real photo month range ("volunteer
          // photos Jul 2019 – Nov 2023"). Un-enriched OLM rows keep the
          // snapshot ingest date instead of a fake relative age. Every
          // other source keeps its real relative date. See olm-popup.ts.
          const whenLine = isOlm ? olmWhenLine(p) : ageStr;
          // dc311 pins get their own template (real observed_at → true
          // relative date, repeat-count hotspot line, dataset link).
          // The generic template lives in obs-popup.ts — shared with the
          // tile-mode path so both engines render identical copy.
          return isDc311
            ? buildDc311PopupHtml(f, { wsBadgeHtml: wsBadge })
            : buildGenericObsPopupHtml(f, {
                styleLabel: style.label,
                wsBadgeHtml: wsBadge,
                isMine,
                whenLine,
                ledgerHtml,
                alreadyReported: reportedIds.has(String(f.id)),
              });
        };
        marker.bindPopup(buildPopupHtml(), { maxWidth: 320 });
        // Accessible name so the marker is announced (and findable in
        // tests). The pile <path> used to carry this via pileAriaLabel,
        // which named a magnitude bucket and a composition profile —
        // neither of which the mark encodes any more, so neither is
        // claimed here. It names the PLACE when the record has one
        // (that is what a reader is looking for) and falls back to the
        // category. role=button is required so axe doesn't flag
        // aria-label as prohibited on a bare <path>.
        const markerEl = marker.getElement?.();
        if (markerEl) {
          const placeName =
            (typeof d.place_name === 'string' && d.place_name) ||
            (typeof d.title === 'string' && d.title) ||
            null;
          markerEl.setAttribute('role', 'button');
          markerEl.setAttribute(
            'aria-label',
            `${style.label}: ${placeName ?? String(p.category ?? 'observation').replace(/_/g, ' ')}`,
          );
        }
        if (!isDc311) wireCleanupVerbButton(marker);
        // Register for the region spotlight (class toggling on click).
        plainMarkers.push({ marker, huc12: resolvedHuc });
        // Issue #69: register the popup rebuilder for the optimistic
        // post-log ledger update. dc311 pins carry no ledger (their
        // template has no verb), so there is nothing to refresh there.
        if (!isDc311) {
          ledgerPopupRefreshers.set(obsId, { props: p, refresh: buildPopupHtml });
        }
      });

      // ─── Cleanup Ledger P1: verb-button wiring ─────────────────────────
      //
      // Popups are plain HTML strings bound via Leaflet's bindPopup — not
      // Preact components — so the verb button rendered by
      // buildCleanupLedgerHtml carries no inline onclick (can't reach
      // ES-module scope from an HTML string). Instead: attach a
      // `popupopen` listener on the marker (same idiom already used
      // elsewhere in this file, e.g. the watershed-polygon spotlight
      // listener above) that activates the button in the freshly-created
      // popup DOM node. The flow itself (bucket picker, org selector,
      // logCleanupEvent, after-photo step) lives in the SHARED
      // lib/cleanup-verb module — dynamic-imported at click time by
      // wireCleanupVerbButtonIn — so the tile-mode path drives the
      // identical interaction (issue #78) and none of it rides the
      // entry chunk (§10).
      function wireCleanupVerbButton(marker: any) {
        // Engine-specific glue handed to the shared flow: closePopup
        // (Leaflet marker) + the #69 post-log optimistic refresh.
        // Self-referential so the refresh can re-wire the freshly-
        // swapped popup DOM with this SAME host.
        const host: CleanupVerbHost = {
          closePopup: () => marker.closePopup?.(),
          onLogged: (info) => applyOptimisticCleanup(info, marker, host),
        };
        marker.on('popupopen', (e: any) =>
          wireCleanupVerbButtonIn(e.popup?.getElement?.(), host),
        );
      }

      // ─── Cleanup Ledger: post-log optimistic update (issue #69) ───────
      //
      // Reciprocity is a stated release principle: the contributor's
      // pickup must be visible the moment the claim succeeds, not after
      // the next manifest poll. The claim-application logic (server-echo
      // vs bucketMidpointItems mirror) lives in cleanup-optimistic.ts,
      // dynamic-imported here so the entry chunk never carries it (§10
      // budget — same pattern as org-select). Best-effort by design: the
      // claim is already durable server-side, so a failed import/refresh
      // must never surface as a logging error — cleanup-verb.ts guards
      // its onLogged call for the same reason.
      async function applyOptimisticCleanup(
        info: CleanupLoggedInfo,
        marker: any,
        host: CleanupVerbHost,
      ) {
        try {
          const entry = ledgerPopupRefreshers.get(info.observationId);
          if (!entry) return;
          const { applyCleanupToProps } = await import('../lib/cleanup-optimistic');
          applyCleanupToProps(entry.props, info.observationId, info.bucket, info.logged);
          const html = entry.refresh();
          marker?.setPopupContent?.(html);
          // setPopupContent swaps the content node while the popup
          // stays open — popupopen won't re-fire until the next open,
          // so re-wire the fresh verb button here with the SAME host
          // (obs-popup's dataset.wired guard makes this idempotent).
          wireCleanupVerbButtonIn(marker?.getPopup?.()?.getElement?.(), host);
        } catch {
          /* popup shows the server copy on the next manifest refresh */
        }
      }

      // openBucketPicker / showAfterPhotoStep moved VERBATIM to
      // src/lib/cleanup-verb.ts (issue #78) — shared with the tile-mode
      // path and loaded lazily at verb-click via wireCleanupVerbButtonIn.
      // The #55 volunteers/hours payload spread and the #69 onLogged
      // hook moved there with them.

      // Legend used to be a Leaflet bottomright control built imperatively
      // from innerHTML. It's now rendered as a Preact <LegendBody /> inside
      // a <MapSheet /> modal triggered by the bottom-right info button.
      // See the JSX further down in this return statement.

      // Zoom control at top-RIGHT (not the Leaflet default top-left) so the
      // HUC-12 watershed hook can occupy top-left without being clipped on
      // mobile. Bigger buttons for thumb taps — Leaflet's defaults are 30px,
      // well under the 44px Apple HIG / WCAG min hit target.
      L.control.zoom({ position: 'topright' }).addTo(map);

      // Persist viewport (#20) + dynamic-pill (#24) into the URL on
      // every pan/zoom. Both share the same 250ms debounce so a rapid
      // drag triggers ONE replaceState + ONE ctx swap, not N.
      let urlWriteTimer: number | undefined;
      const persistViewportAndPill = () => {
        if (urlWriteTimer !== undefined) window.clearTimeout(urlWriteTimer);
        urlWriteTimer = window.setTimeout(() => {
          const c = map.getCenter();

          // #24: dynamic pill on pan. Point-in-polygon against the
          // loaded watersheds — if the map center is inside one of the
          // 16 loaded HUC-12s, swap ctx/impairment to that one. If the
          // user panned outside coverage, null ctx + URL clears ?huc=.
          const found = findContainingWatershed(c.lat, c.lng, watershed);
          if (found) {
            const wsCtx = (found.feature.properties as { watershed_context?: WatershedContextDoc | null }).watershed_context;
            const impStat = (found.feature.properties as { impairment_status?: ImpairmentDoc | null }).impairment_status;
            // Only setState if the active HUC actually changed —
            // avoids re-render-churn on every pan within the same HUC.
            // ctxRef (not ctx): this closure is bound once per map
            // instance, so the state value would be permanently stale.
            if (wsCtx && wsCtx.huc12 !== ctxRef.current?.huc12) {
              setCtx(wsCtx);
              setImpairment(impStat ?? null);
            }
            writeMapStateToUrl({
              zoom: map.getZoom(),
              lat: c.lat,
              lon: c.lng,
              huc12: found.huc12,
            });
          } else {
            if (ctxRef.current !== null) {
              setCtx(null);
              setImpairment(null);
            }
            writeMapStateToUrl({
              zoom: map.getZoom(),
              lat: c.lat,
              lon: c.lng,
              huc12: null, // clear stale ?huc= from URL
            });
          }
        }, 250);
      };
      map.on('moveend', persistViewportAndPill);
      map.on('zoomend', persistViewportAndPill);

      // Teardown for THIS map instance — the effect cleanup runs it when
      // the verified toggle re-inits on the same container, when a
      // manifest bump swaps containers, and on unmount. Without it every
      // re-init stranded the previous Leaflet map: ~170 marker/popup DOM
      // nodes and the window `resize` listener Leaflet registers for
      // trackResize — and the replacement map could snap back to a
      // ≤250ms-stale URL viewport (the debounce above). The mountain-SVG
      // teardown that used to live here went out with the overlay.
      disposeMap = () => {
        // Flush the debounced URL write with the CURRENT viewport so the
        // next init re-opens exactly where the user left this map. Skip
        // when we're unmounting because of a route change — the new
        // route's URL must not inherit ?z=&c=.
        if (urlWriteTimer !== undefined) window.clearTimeout(urlWriteTimer);
        if (window.location.pathname.endsWith('/explore')) {
          const c = map.getCenter();
          writeMapStateToUrl({
            zoom: map.getZoom(),
            lat: c.lat,
            lon: c.lng,
            huc12: findContainingWatershed(c.lat, c.lng, watershed)?.huc12 ?? null,
          });
        }
        if ((window as any)._dregsbaneTrailMap === map) {
          delete (window as any)._dregsbaneTrailMap;
        }
        if (legacyMapRef.current === map) legacyMapRef.current = null;
        // Releases all layers/popups/DOM and the window resize listener,
        // and clears the container's _leaflet_id so the SAME div can
        // host the next instance (verified-toggle path).
        map.remove();
      };
    }
    // showUnverified / timeWindow: toggling either re-runs this effect,
    // whose cleanup (disposeMap) tears the old Leaflet instance down and
    // re-inits on the same container with the new filter(s) — the single
    // mechanism for both toggles. ctx is deliberately NOT a dep: it
    // changes on every cross-boundary pan (dynamic pill) and would
    // destroy/recreate the map mid-use; the imperative closures read it
    // via ctxRef instead.
  }, [observations, watershed, stations, showUnverified, timeWindow]);

  // ─── Header text — every claim sourced from a fetched fixture ──────────
  // Q4: aggregate counts respect the verified-only toggle, via the SAME
  // rendersOnMap predicate as the marker loop and the popup's K — so
  // "{n} in view" / "showing {K}" / "+{M} elsewhere" count exactly the
  // records that draw a circle marker or a pile. (Notably: tag-less OLM
  // records render nothing and are excluded here too.) Memoize so the
  // filter doesn't re-run on every render — also lets the JSX downstream
  // read a stable array.
  const visibleFeatures = useMemo(() => {
    if (!observations) return [];
    return observations.features.filter((f) =>
      rendersOnMap(f, showUnverified, myRecentIds, timeWindow),
    );
  }, [observations, showUnverified, myRecentIds, timeWindow]);
  // All-time (window='all') visible population — same verified filter,
  // no time-window narrowing. Only used to compute the "(+N undated)"
  // pill note below; every other count stays window-scoped.
  const allTimeVisibleFeatures = useMemo(() => {
    if (!observations) return [];
    return observations.features.filter((f) =>
      rendersOnMap(f, showUnverified, myRecentIds, 'all'),
    );
  }, [observations, showUnverified, myRecentIds]);
  // Tile mode: the controller counts the rendered viewport population
  // (record-unit — a bake-side cluster stands for point_count records).
  const obsCount = tileConfig ? tileStats.visibleCount : visibleFeatures.length;
  const sourcesAttempted =
    (observations?.metadata as any)?.sources_attempted_but_no_data ?? [];
  const sourcesReturning =
    (observations?.metadata as any)?.sources_returning_data ?? [];

  // Three pill states (closes #24):
  //   - loading        — manifest not yet fetched (initial mount)
  //   - inside-a-HUC   — ctx populated; show watershed sentence
  //   - outside        — manifest loaded but the map center is not
  //                      inside any loaded HUC polygon
  const isLoading = manifestVersion === null && ctx === null;
  // "Outside coverage" = nothing to show in the pill. In tile mode a
  // hit-tested watershed WITHOUT api context (uncovered HUC) still
  // populates the pill from tile properties, so it is not "outside".
  const isOutsideCoverage =
    manifestVersion !== null && ctx === null && !(tileConfig && tileActive);

  // Active watershed feature for the pill (one geometry, one population).
  const activeWatershedFeature = useMemo(() => {
    if (!ctx || !watershed) return null;
    return (
      (watershed.features as Array<{
        geometry: { type: string; coordinates: unknown };
        properties: Record<string, unknown>;
      }>).find((f) => String(f.properties?.huc12 ?? '') === ctx.huc12) ?? null
    );
  }, [ctx, watershed]);

  // Authoritative recorded total for the active watershed —
  // properties.obs_count, stamped server-side (counts ALL records
  // regardless of verified). Pre-backfill fallback: the SAME
  // stamped-first membership rule as K (membersOfWatershed) applied to
  // ALL fetched records — identical to the polygon-click popup's
  // fallback, so the chip and the popup can never disagree.
  const legacyRecordedCount = useMemo(() => {
    if (!activeWatershedFeature) return 0;
    const oc = (activeWatershedFeature.properties as { obs_count?: unknown }).obs_count;
    if (typeof oc === 'number') return oc;
    if (!observations) return 0;
    const huc12 = String(
      (activeWatershedFeature.properties as { huc12?: unknown }).huc12 ?? '',
    );
    return countRecords(
      membersOfWatershed(observations.features, huc12, activeWatershedFeature.geometry),
    );
  }, [activeWatershedFeature, observations]);

  // K for the pill — member records among currently-visible features
  // (stamped huc12 trusted; ray-cast only for null stamps). Matches the
  // popup's "showing {K} on the map now" population exactly.
  const legacyShowingCount = useMemo(() => {
    if (!ctx || !activeWatershedFeature) return 0;
    return countRecords(
      membersOfWatershed(visibleFeatures, ctx.huc12, activeWatershedFeature.geometry),
    );
  }, [ctx, activeWatershedFeature, visibleFeatures]);

  // Cleanup Ledger P1 pill aggregate: properties.cleanup_count is a pure
  // best-effort-ADD counter (incrementWatershedCleanupCount), same
  // maintenance idiom as obs_count — no per-record membership fallback
  // needed since it's never derived from the loaded feature collection.
  // Legacy Leaflet mode only for P1 (tile mode's bake pipeline doesn't
  // carry this field yet — see cleanup-ledger-spec.md P1 scope).
  const legacyCleanupCount = useMemo(() => {
    if (!activeWatershedFeature) return 0;
    const cc = (activeWatershedFeature.properties as { cleanup_count?: unknown }).cleanup_count;
    return typeof cc === 'number' ? cc : 0;
  }, [activeWatershedFeature]);

  // "(+N undated)" pill note: records that WOULD render under 'all time'
  // (verified filter already applied) but have no trustworthy date, so
  // the current time-window can't place them and hides them outright —
  // they must be surfaced as a count, never silently dropped. Zero
  // whenever the window is 'all' (nothing is hidden) or ctx is absent.
  const activeUndatedHiddenCount = useMemo(() => {
    if (!ctx || !activeWatershedFeature || timeWindow === 'all') return 0;
    const allTimeMembers = membersOfWatershed(
      allTimeVisibleFeatures,
      ctx.huc12,
      activeWatershedFeature.geometry,
    );
    return allTimeMembers.filter((f) => isUndated(f)).length;
  }, [ctx, activeWatershedFeature, allTimeVisibleFeatures, timeWindow]);

  // Year-bar strip for the expanded pill — same builder the watershed
  // popup uses (see watershedPopupHtml in the map-init effect), fed by
  // the verified-filter-respecting/time-window-IGNORING population so
  // the bars stay the time context regardless of the chip selected.
  const activeYearBarHtml = useMemo(() => {
    if (!ctx || !activeWatershedFeature || !observations) return '';
    const members = membersOfWatershed(
      observations.features.filter((f) => rendersOnMap(f, showUnverified, myRecentIds, 'all')),
      ctx.huc12,
      activeWatershedFeature.geometry,
    );
    if (members.length === 0) return '';
    return buildYearBarHtml(computeYearBars(members), pastVsPriorYear(members));
  }, [ctx, activeWatershedFeature, observations, showUnverified, myRecentIds]);

  // Tile mode reads both numbers off the hit-tested tile feature: the
  // recorded total is the baked `obs_count` property (authoritative),
  // "showing K" counts the rendered member markers.
  const activeRecordedCount = tileConfig
    ? tileActive?.obsCount ?? 0
    : legacyRecordedCount;
  const activeShowingCount = tileConfig
    ? tileActive?.showingCount ?? 0
    : legacyShowingCount;

  // Visible records NOT in the active watershed. Record-unit, like
  // every other number on this surface.
  const elsewhereCount = Math.max(0, obsCount - activeShowingCount);

  // Pill sentence — SAME per-watershed count as the chip (obs_count of
  // the active watershed), "showing {K}" semantics consistent with the
  // popup, "+{M} elsewhere on the map". Zero-branch keys on the
  // WATERSHED count, not the global count.
  // "(+N undated)" note: only meaningful when the time-window is
  // actually hiding undated records — appended to whichever ctx-branch
  // sentence renders (empty vs. non-empty), never to the tile-mode or
  // outside-coverage branches (no per-record data there).
  const undatedNote = activeUndatedHiddenCount > 0 ? ` (+${activeUndatedHiddenCount} undated)` : '';
  // "removals covered ~Y% of new litter here this year" — P1 approximates
  // "this year" as the watershed's all-time recorded count (activeRecordedCount)
  // rather than a year-scoped denominator; refining to a true year-scoped
  // count is future work, not fabricated precision (cleanup_count itself
  // is real, only the "this year" framing is an approximation). Omitted
  // entirely when there's nothing recorded yet (0/0 is not a percentage)
  // or nothing has ever been logged as cleaned (no claim to report).
  const activeCleanupCoveragePct =
    activeRecordedCount > 0 && legacyCleanupCount > 0
      ? Math.round((100 * legacyCleanupCount) / activeRecordedCount)
      : null;
  const cleanupCoverageClause =
    activeCleanupCoveragePct !== null
      ? ` Removals covered ~${activeCleanupCoveragePct}% of new litter here this year.`
      : '';
  const hookSentence = ctx
    ? activeRecordedCount === 0
      ? `0 litter reports recorded in the ${ctx.name} watershed yet${elsewhereCount > 0 ? ` (+${elsewhereCount} elsewhere on the map)` : ''}.${undatedNote} This watershed drains to the ${ctx.drains_to.intake_name}, which supplies drinking water to ${ctx.drains_to.served_communities.join(' and ')}.`
      : `${activeRecordedCount} litter report${activeRecordedCount === 1 ? '' : 's'} recorded in the ${ctx.name} watershed${activeShowingCount !== activeRecordedCount ? ` — showing ${activeShowingCount} on the map now` : ''}${elsewhereCount > 0 ? `, +${elsewhereCount} elsewhere on the map` : ''}${undatedNote} — upstream of the ${ctx.drains_to.intake_name} serving ${ctx.drains_to.served_communities.join(' and ')}.${cleanupCoverageClause}`
    : tileConfig && tileActive
      // Tile mode, UNCOVERED watershed: national tiles name every HUC-12
      // and carry its recorded count, but the drinking-water context doc
      // only exists for covered HUCs (graceful absence per contract) —
      // same count copy, no drains-to sentence.
      ? activeRecordedCount === 0
        ? `0 litter reports recorded in the ${tileActive.name} watershed yet${elsewhereCount > 0 ? ` (+${elsewhereCount} elsewhere on the map)` : ''}.`
        : `${activeRecordedCount} litter report${activeRecordedCount === 1 ? '' : 's'} recorded in the ${tileActive.name} watershed${activeShowingCount !== activeRecordedCount ? ` — showing ${activeShowingCount} on the map now` : ''}${elsewhereCount > 0 ? `, +${elsewhereCount} elsewhere on the map` : ''}.`
      : isOutsideCoverage
        ? tileConfig
          // National tiles: coverage is CONUS-wide, so the legacy
          // "16 watersheds around DC" line would be false here.
          ? 'This spot isn\'t inside a mapped watershed. Pan or zoom to see per-watershed litter counts and water context.'
          : 'This area isn\'t mapped yet. We currently cover 16 watersheds around Washington, DC and Fredericksburg, VA — pan back to see litter counts and water context.'
        : 'Loading…';

  // Pill chip text — leads with the readable name + the per-watershed
  // recorded count ("{name} · {n} reports"). When the map center isn't
  // inside any loaded watershed, fall back to the visible total.
  // Water status of the active watershed — leads the pill in color, same
  // source (impairment doc) as the expanded banner so the two never
  // disagree.
  //
  // Tile-mode water parity (issue #78): prefer the one-shot versioned
  // /watersheds impairment doc for the hit-tested HUC — the identical
  // source the legacy path inlines — falling back to whatever the
  // per-HUC context endpoint parsed (usually null: it embeds a summary,
  // not the full doc). Legacy mode is byte-identical: tileConfig is
  // null, so activeImpairment === impairment. Banner, chip, and the
  // Sources sheet all read this ONE value so no pair can disagree.
  const activeImpairment =
    (tileConfig && activeTileHuc
      ? tileImpairmentByHuc?.get(activeTileHuc) ?? null
      : null) ?? impairment;
  const activeStatus: WaterStatus = waterStatusOf(activeImpairment);
  const pillChipText = ctx
    ? `${ctx.name} · ${activeRecordedCount} report${activeRecordedCount === 1 ? '' : 's'}`
    : tileConfig && tileActive
      ? `${tileActive.name} · ${activeRecordedCount} report${activeRecordedCount === 1 ? '' : 's'}`
      : isOutsideCoverage
        ? `${obsCount} in view`
        : 'Loading…';

  // "Not yet assigned" chip (pre-backfill only): visible records whose
  // huc12 stamp is null but which fall inside a loaded polygon. Depends
  // only on the data version + verified toggle — never recomputes on pan.
  const unassignedCount = useMemo(() => {
    if (!watershed) return 0;
    return countUnassignedInMapped(visibleFeatures, watershed.features ?? []);
  }, [visibleFeatures, watershed]);

  const impairmentBanner =
    activeImpairment && activeImpairment.impaired_assessment_units.length > 0 ? (
      <p style={impairmentStyle}>
        ⚠ {activeImpairment.huc12_name} contains{' '}
        <strong>
          {activeImpairment.impaired_assessment_units.length} impaired{' '}
          {activeImpairment.impaired_assessment_units.length === 1 ? 'waterbody' : 'waterbodies'}
        </strong>{' '}
        according to EPA&rsquo;s {activeImpairment.reporting_cycle} water-quality
        assessment (ATTAINS). Top causes:{' '}
        {activeImpairment.top_causes
          .slice(0, 3)
          .map((c) => displayCause(c.cause))
          .join(', ')}
        .{' '}
        <a href={activeImpairment.source_url} target="_blank" rel="noopener" style={linkStyle}>
          source ↗
        </a>
      </p>
    ) : null;

  // Key the map container off manifestVersion ONLY, so a version bump
  // (ingest run or accepted submission) swaps in a fresh container and
  // the init effect (observations dep) rebuilds the map for the new
  // data. The verified-filter toggle deliberately does NOT remount the
  // container: it re-runs the init effect (showUnverified dep), whose
  // cleanup (disposeMap) removes the old Leaflet instance and re-inits
  // on the SAME div — one mechanism, no stranded maps. In both paths the
  // teardown flushes the debounced URL write first, so the replacement
  // map re-opens at the user's current pan/zoom instead of a stale one.
  // A smarter incremental-marker-update path is tracked as a follow-up —
  // refactoring the imperative Leaflet renderer to diff observations vs.
  // existing markers is substantial; this controlled re-init keeps the
  // blast radius small while still honoring the "within seconds" SLA.
  //
  // TILE MODE IS DIFFERENT, and the key has to say so. Keying the
  // container off manifestVersion there would throw the div away on
  // every version bump — remounting MapLibre, re-reading both PMTiles
  // archives and destroying any open popup — which is exactly what the
  // incremental /fresh path exists to avoid. In tile mode the container
  // is keyed off the TILE URLS, so only a real re-bake gets a fresh div.
  const mapKey = tileConfig ? `tiles:${tileKey}` : manifestVersion ?? 'pre-load';
  return (
    <div style={shellStyle}>
      <div key={mapKey} ref={mapEl} style={mapStyle} />
      <details style={hookStyle} className="trash-trail-hook">
        <summary style={hookSummaryStyle} className="trash-trail-hook__summary">
          <span style={badgeStyle}>
            📍 {pillChipText}
            {ctx && WATER_STATUS_SHORT[activeStatus] && (
              <span
                style={{
                  marginLeft: 6,
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '1px 7px',
                  borderRadius: 100,
                  whiteSpace: 'nowrap',
                  textTransform: 'uppercase',
                  letterSpacing: '0.02em',
                  // Ink, not the map colour. The map's water blue is
                  // tuned as a POLYGON STROKE (4.03:1, clearing the 3:1
                  // graphical floor) and measures only 4.34:1 as TEXT on
                  // the dark pill — under SC 1.4.3's 4.5:1. waterInkFor()
                  // swaps in the text-only tint for exactly that rung;
                  // the border and wash stay the map colour, so the chip
                  // still points at the polygon it describes.
                  color: waterInkFor(activeStatus),
                  background: `${WATER_STATUS_COLOR[activeStatus]}22`,
                  border: `1px solid ${WATER_STATUS_COLOR[activeStatus]}`,
                }}
              >
                {WATER_STATUS_SHORT[activeStatus]}
              </span>
            )}
          </span>
          <span style={hookChevronStyle} aria-hidden="true">▾</span>
        </summary>
        <div style={hookBodyStyle}>
          {(ctx || tileActive) && (
            <p style={{ ...hookSentenceStyle, fontSize: 11, opacity: 0.7, fontWeight: 400, margin: '0 0 8px' }}>
              USGS watershed ID {ctx?.huc12 ?? tileActive!.huc12}
              {(ctx?.state ?? tileActive?.state) ? ` · ${stateFullName((ctx?.state ?? tileActive?.state)!)}` : ''}
            </p>
          )}
          <p style={hookSentenceStyle}>{hookSentence}</p>
          {impairmentBanner}
          {/* Per-watershed change readout — same builder + member
              population the Leaflet popup uses (year-bars.ts), so the
              two surfaces can never disagree. Ignores the time-window
              on purpose: the bars ARE the time context. */}
          {activeYearBarHtml && (
            <div dangerouslySetInnerHTML={{ __html: activeYearBarHtml }} />
          )}
        </div>
      </details>
      <ContributeFab />
      {/* Zoom-ladder readout bar (issue #87): tile mode only, lazily
          imported with the map chunk. Null stats ⇒ renders nothing. */}
      {tileConfig && ReadoutBarLazy && <ReadoutBarLazy stats={tileReadout} />}
      {tileConfig && ParkChipLazy && (
        <ParkChipLazy
          park={activePark}
          // Tapping the chip opens the mission sheet, which is where the
          // "be the first here" ask already lives — the chip states the
          // fact, the sheet is what you do about it.
          onOpen={() => openMissions()}
        />
      )}
      {/* Firefly calendar range + the two layer toggles: TILE MODE ONLY,
          lazily imported with the map chunk. Every prop is a plain
          string/number/boolean — the component never imports firefly-data,
          so the buffer builder can't leak into the entry chunk. Changing
          any of these does NOT re-init the map (see the filter effect). */}
      {tileConfig && MapTimeControlsLazy && (
        <MapTimeControlsLazy
          fromIso={fromIso}
          toIso={toIso}
          minIso={fireflyStats?.minIso ?? ''}
          maxIso={fireflyStats?.maxIso ?? ''}
          showLitter={showLitterLayer}
          showCleanups={showCleanupLayer}
          litCount={fireflyStats?.litCount ?? 0}
          litCleanupCount={fireflyStats?.litCleanupCount ?? 0}
          cleanupCount={fireflyStats?.cleanupCount ?? 0}
          emberCount={fireflyStats?.emberCount ?? 0}
          undatedCount={fireflyStats?.undatedCount ?? 0}
          onRangeChange={(nextFrom, nextTo) => {
            setFromIso(nextFrom);
            setToIso(nextTo);
            // A manual pick retires the one-shot auto-seed, so a later
            // pan that widens the data bounds can't stomp the choice.
            rangeSeededRef.current = true;
          }}
          onToggleLitter={setShowLitterLayer}
          onToggleCleanups={setShowCleanupLayer}
          timeline={{
            playheadIso: timelinePlayheadIso,
            playing: timelinePlaying,
            daysPerSec: timelineSpeed,
          }}
          onTimelinePlay={setTimelinePlaying}
          onTimelineScrub={onTimelineScrub}
          onTimelineSpeed={setTimelineSpeed}
          onTimelineOff={onTimelineOff}
        />
      )}
      {/* Time-window control: legacy-Leaflet-only. Tile mode has the
          calendar above instead — three fixed buckets can't express
          "the week of the cleanup". */}
      {!tileConfig && (
        <TimeWindowControl value={timeWindow} onChange={onChangeTimeWindow} />
      )}
      {toast && (
        <div role="status" class="trail-toast">
          {toast}
        </div>
      )}
      {unassignedCount > 0 && (
        <div class="trail-unassigned-chip" role="status">
          {unassignedCount} observation{unassignedCount === 1 ? '' : 's'} not
          yet assigned to a watershed
        </div>
      )}
      {error && (
        <div style={errorStyle}>Failed to load Trash Trail data: {error}</div>
      )}

      {/*
        Two round corner buttons stacked vertically above the FAB on the
        right. Each opens a bottom-sheet modal (LegendBody / SourcesBody).
        Pattern matches Apple Maps / AllTrails / SAP Fiori — the map
        surface stays clean and the affordances stay reachable.
      */}
      <MapCornerControls
        onMissionsClick={openMissions}
        onLegendClick={() => setOpenSheet('legend')}
        onSourcesClick={() => setOpenSheet('sources')}
        onLocateClick={toggleLocate}
        locateState={locateState}
      />
      <MapSheet
        open={openSheet === 'legend'}
        onClose={() => setOpenSheet(null)}
        title="Legend"
      >
        <LazyLegendBody
          sourcesInData={sourcesInData}
          showUnverified={showUnverified}
          onToggleShowUnverified={onToggleShowUnverified}
        />
      </MapSheet>
      <MapSheet
        open={openSheet === 'sources'}
        onClose={() => setOpenSheet(null)}
        title="Sources"
      >
        <LazySourcesBody
          ctx={ctx}
          debugMode={debugMode}
          sourcesAttempted={sourcesAttempted}
          impairment={activeImpairment}
          commons={commonsConfig}
          onOpenIntro={() => setOpenSheet('intro')}
        />
      </MapSheet>
      <MapSheet
        open={openSheet === 'intro'}
        onClose={dismissIntro}
        title="Welcome to Trash Trail"
      >
        <IntroBody onDismiss={dismissIntro} />
      </MapSheet>
      {/* "Start here" mission surface (issue #94): First Look + Comeback
          Spot, computed from data already loaded (legacy props) or a
          user-initiated on-demand fetch (tile mode). Same bottom-sheet
          chrome as Legend/Sources — suggests, never blocks. */}
      <MapSheet
        open={openSheet === 'missions'}
        onClose={() => setOpenSheet(null)}
        title="Start here"
      >
        <LazyStartHereBody
          manifestVersion={manifestVersion}
          observations={observations}
          watersheds={watershed}
          mapCenter={missionsCenter}
          parks={missionsParks}
          defaultCenter={{ lat: DEFAULT_MAP_LANDING.lat, lon: DEFAULT_MAP_LANDING.lon }}
          onShowOnMap={showMissionOnMap}
        />
      </MapSheet>
    </div>
  );
}

/**
 * First-visit orientation card ("Welcome to Trash Trail"). Rendered in
 * the same MapSheet bottom-sheet as Legend/Sources — zero new visual
 * pattern. Auto-opens once per browser (see intro-gate.ts); re-openable
 * from the Sources sheet's "About this map" row.
 */
function IntroBody({ onDismiss }: { onDismiss: () => void }) {
  return (
    <div class="trail-intro">
      <p class="trail-intro__sub">
        A live map of litter on its way to local creeks — and the drinking
        water they feed.
      </p>
      <ul class="trail-intro__points">
        <li>
          <strong>The mounds are trash.</strong> Bigger mound = more litter
          photographed there. Tap one to see what it&rsquo;s made of.
        </li>
        <li>
          <strong>Grey outlines are watersheds.</strong> Rain washes litter
          from each outlined area into one creek. The top-left banner names
          the water supply downstream.
        </li>
        <li>
          <strong>Add what you see.</strong> Tap &ldquo;Report litter&rdquo;,
          snap a photo. Reports are reviewed by hand before they count.
        </li>
        <li>
          <strong>Every number is cited.</strong> Data: OpenLitterMap
          volunteers, DC 311 reports, Reddit, local news, EPA &amp; USGS.
          Tap ⓘ (bottom right) for sources.
        </li>
      </ul>
      <button type="button" class="trail-intro__cta" onClick={onDismiss}>
        Explore the map
      </button>
    </div>
  );
}

// ─── Map overlay sheets: Legend + Sources ────────────────────────────
// Replaces the previous bottom-anchored Leaflet legend control + Sources
// <footer>. Pattern reference: Apple Maps / AllTrails / SAP Fiori — two
// corner icon buttons each open a bottom-sheet modal. Map surface stays
// uncluttered; mobile users still reach attribution + key.

interface MapSheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: preact.ComponentChildren;
}

/**
 * Bottom-sheet modal: full-width on phones, max-width 480px on desktop.
 * Anchored to the bottom of the viewport, slides up when open. Tap the
 * backdrop or the close button to dismiss. Escape key also dismisses.
 *
 * Body scrolls internally if content overflows the 70vh max-height; the
 * sheet header (title + close button) stays sticky at the top.
 */
function MapSheet({ open, onClose, title, children }: MapSheetProps) {
  // Esc-to-close, scoped to the lifetime of an open sheet.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div class="map-sheet" role="dialog" aria-modal="true" aria-labelledby="map-sheet-title">
      <div class="map-sheet__backdrop" onClick={onClose} aria-hidden="true" />
      <div class="map-sheet__panel">
        <div class="map-sheet__handle" aria-hidden="true" />
        <header class="map-sheet__header">
          <h2 id="map-sheet-title" class="map-sheet__title">{title}</h2>
          <button
            type="button"
            class="map-sheet__close"
            aria-label="Close"
            onClick={onClose}
          >
            ✕
          </button>
        </header>
        <div class="map-sheet__body">{children}</div>
      </div>
    </div>
  );
}

/**
 * "All time" / "Past year" / "Past 90 days" segmented control —
 * trail #temporal-insight. Bottom-left (lifted above the "not yet
 * assigned" chip's slot so the two never overlap), matching the dark
 * gold-on-charcoal chrome the corner controls / FAB already use.
 * Drives `timeWindow`, which feeds rendersOnMap identically everywhere
 * (map markers, popup K, pill chip/sentence, spotlight) via the map
 * re-init effect — one state, one predicate, every count agrees.
 */
function TimeWindowControl({
  value,
  onChange,
}: {
  value: TimeWindow;
  onChange: (next: TimeWindow) => void;
}) {
  const options: Array<{ key: TimeWindow; label: string }> = [
    { key: 'all', label: 'All time' },
    { key: '1y', label: 'Past year' },
    { key: '90d', label: 'Past 90 days' },
  ];
  return (
    <div class="trail-timewindow" role="group" aria-label="Time window">
      {options.map((o) => (
        <button
          key={o.key}
          type="button"
          class={
            'trail-timewindow__btn' +
            (value === o.key ? ' trail-timewindow__btn--active' : '')
          }
          aria-pressed={value === o.key}
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Round 44×44 controls stacked vertically. Sits at bottom-right, lifted
 * above the FAB's center so the stack and the FAB read as a single
 * bottom-row cluster. Hit targets meet WCAG 2.5.5 / Apple HIG.
 *
 * Topmost slot is the account button, which renders only when signed in
 * — the column is bottom-anchored, so its appearance pushes nothing
 * else around.
 */
function MapCornerControls({
  onMissionsClick,
  onLegendClick,
  onSourcesClick,
  onLocateClick,
  locateState,
}: {
  onMissionsClick: () => void;
  onLegendClick: () => void;
  onSourcesClick: () => void;
  onLocateClick: () => void;
  locateState: LocateState;
}) {
  const locateLabel =
    locateState === 'locating'
      ? 'Finding your location…'
      : locateState === 'denied'
        ? 'Location permission denied — enable it in your browser settings'
        : locateState === 'unavailable'
          ? 'Location unavailable on this device'
          : locateState === 'on'
            ? 'Hide your location'
            : 'Show my location';
  return (
    <div class="map-corner-controls">
      <AccountCornerButton />
      <div class="map-corner-controls__item">
        <button
          type="button"
          class="map-corner-controls__btn"
          aria-label={locateLabel}
          title={locateLabel}
          aria-pressed={locateState === 'on'}
          onClick={onLocateClick}
        >
          {/* crosshair — the universal "locate me" affordance */}
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true"
               fill="none" stroke="currentColor" stroke-width="2"
               stroke-linecap="round">
            <circle cx="12" cy="12" r="7"
                    opacity={locateState === 'locating' ? 0.45 : 1} />
            <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none"
                    opacity={locateState === 'on' ? 1 : 0.55} />
            <path d="M12 1.5v3M12 19.5v3M1.5 12h3M19.5 12h3" />
          </svg>
        </button>
        <span class="map-corner-controls__caption" aria-hidden="true">
          {locateState === 'locating' ? 'Finding…' : 'Locate'}
        </span>
      </div>
      <div class="map-corner-controls__item">
        <button
          type="button"
          class="map-corner-controls__btn"
          aria-label="Start here — nearby missions"
          onClick={onMissionsClick}
        >
          {/* flag icon — "here's a place to start" affordance */}
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M6 2a1 1 0 0 0-1 1v18a1 1 0 1 0 2 0v-6.6h11.2a.8.8 0 0 0 .66-1.25L15.9 8.9l2.96-4.25A.8.8 0 0 0 18.2 3.4H7V3a1 1 0 0 0-1-1Zm1 3.4h9.67l-2.4 3.44a.8.8 0 0 0 0 .92l2.4 3.44H7V5.4Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <span class="map-corner-controls__caption" aria-hidden="true">Start</span>
      </div>
      <div class="map-corner-controls__item">
        <button
          type="button"
          class="map-corner-controls__btn"
          aria-label="Legend"
          onClick={onLegendClick}
        >
          {/* layered-squares icon — universal "legend / layers" affordance */}
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M12 3 2 8.5 12 14l10-5.5L12 3Zm0 7.34L4.46 8.5 12 4.66l7.54 3.84L12 10.34ZM2 16.5l10 5.5 10-5.5-2.16-1.2L12 19.4l-7.84-4.1L2 16.5Z"
              fill="currentColor"
            />
          </svg>
        </button>
        {/* Tiny text caption so first-time visitors don't have to guess
            what the icon opens (the layers glyph reads "basemap switch"
            in most map apps). aria-hidden: the button's aria-label
            already names it for AT. */}
        <span class="map-corner-controls__caption" aria-hidden="true">Key</span>
      </div>
      <div class="map-corner-controls__item">
        <button
          type="button"
          class="map-corner-controls__btn"
          aria-label="Data sources"
          onClick={onSourcesClick}
        >
          {/* lowercase-i in a circle — universal "info / sources" affordance */}
          <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
            <path
              d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8 8 0 0 1-8 8Zm-1-12.5a1 1 0 1 1 1 1 1 1 0 0 1-1-1ZM13 17h-2v-6h2Z"
              fill="currentColor"
            />
          </svg>
        </button>
        <span class="map-corner-controls__caption" aria-hidden="true">Sources</span>
      </div>
    </div>
  );
}

/**
 * Legend body loads as its own chunk when the legend sheet first opens
 * (MapSheet renders children only while open, so the import can never
 * fire at first paint) — keeps the entry chunk under the 40 KB gate.
 * Same dependency-free lazy pattern as app.tsx's lazyPage; component
 * lives in src/components/LegendBody.tsx.
 */
function LazyLegendBody(props: {
  sourcesInData: Set<string>;
  showUnverified: boolean;
  onToggleShowUnverified: (next: boolean) => void;
}) {
  type LegendComp = typeof import('../components/LegendBody').LegendBody;
  const [Comp, setComp] = useState<LegendComp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    import('../components/LegendBody')
      .then((m) => {
        if (alive) setComp(() => m.LegendBody);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, []);
  if (err) {
    return <p role="alert">Couldn&rsquo;t load the legend ({err}). Check your connection and reload.</p>;
  }
  if (!Comp) return <p aria-live="polite">Loading&hellip;</p>;
  return <Comp {...props} />;
}

function LazySourcesBody(props: {
  ctx: WatershedContextDoc | null;
  debugMode: boolean;
  sourcesAttempted: any[];
  impairment: ImpairmentDoc | null;
  commons: TrailCommonsConfig | null;
  onOpenIntro: () => void;
}) {
  type SourcesComp = typeof import('../components/SourcesBody').SourcesBody;
  const [Comp, setComp] = useState<SourcesComp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    import('../components/SourcesBody')
      .then((m) => {
        if (alive) setComp(() => m.SourcesBody);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, []);
  if (err) {
    return <p role="alert">Couldn&rsquo;t load the sources ({err}). Check your connection and reload.</p>;
  }
  if (!Comp) return <p aria-live="polite">Loading&hellip;</p>;
  return <Comp {...props} />;
}

/**
 * "Start here" body (issue #94) loads as its own chunk when the sheet
 * first opens — same dependency-free lazy pattern as LazyLegendBody, so
 * mission-select + the sheet UI never ride the 40 KB entry chunk.
 */
function LazyStartHereBody(
  props: import('../components/StartHereBody').StartHereBodyProps,
) {
  type StartHereComp = typeof import('../components/StartHereBody').StartHereBody;
  const [Comp, setComp] = useState<StartHereComp | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    import('../components/StartHereBody')
      .then((m) => {
        if (alive) setComp(() => m.StartHereBody);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, []);
  if (err) {
    return <p role="alert">Couldn&rsquo;t load missions ({err}). Check your connection and reload.</p>;
  }
  if (!Comp) return <p aria-live="polite">Loading&hellip;</p>;
  return <Comp {...props} />;
}

function escape(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// ─── Styles (inline; CSS pipeline tonight is overkill) ────────────────────

const shellStyle = {
  position: 'relative' as const,
  height: '100vh',
  background: '#0A0608',
  color: '#f0e6d2',
  fontFamily: 'system-ui, sans-serif',
  overflow: 'hidden' as const,
};

// Hook = collapsible overlay anchored to top-left. Default = small
// chip. Click to expand the full hook sentence + impairment + CTA.
// Map gets the full viewport behind it.
const hookStyle = {
  position: 'absolute' as const,
  top: 10,
  left: 10,
  zIndex: 800,
  // Zoom control moved to top-right (see L.control.zoom call), so the hook
  // only needs to dodge the right-side controls. ~80px is enough for the
  // zoom buttons + a small breathing gap.
  maxWidth: 'calc(100% - 90px)',
  background: 'rgba(10, 6, 8, 0.96)',
  border: '1px solid #3a2e30',
  borderRadius: 8,
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.65)',
};

const hookSummaryStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '8px 12px',
  cursor: 'pointer',
  userSelect: 'none' as const,
  listStyle: 'none',
};

const hookChevronStyle = {
  fontSize: 13,
  color: '#b8a878',
  display: 'inline-block',
};

const hookBodyStyle = {
  padding: '4px 14px 12px',
  borderTop: '1px solid #3a2e30',
  maxHeight: 'calc(100vh - 120px)',
  overflowY: 'auto' as const,
};

const badgeStyle = {
  fontSize: 12,
  letterSpacing: '0.04em',
  color: '#d5c4a4',
  fontWeight: 600 as const,
};

const hookSentenceStyle = {
  margin: '0 0 4px',
  fontSize: 14,
  lineHeight: 1.4,
  fontWeight: 500 as const,
  color: '#f0e6d2',
};

const impairmentStyle = {
  margin: '8px 0 0',
  fontSize: 13,
  // Rust-family ink: the impairment banner speaks about the same state
  // the trash wash paints, so it wears that family's readable ink.
  color: 'var(--tt-ink-rust)',
};

const gapCtaStyle = {
  margin: '10px 0 0',
  fontSize: 13,
  color: '#cfd8dc',
};

/**
 * Text tint for a water status. The wash hues are tuned for low-opacity
 * polygon fills, not 12px text on the dark pill — WATER_STATUS_INK holds
 * the lifted, SC 1.4.3-clearing siblings.
 */
function waterInkFor(status: WaterStatus): string {
  return WATER_STATUS_INK[status];
}

// Vermilion, matching the litter marks: links are the "you can act here"
// channel, and underlines stay because ink-muted sits close enough that
// hue must not be the sole cue.
const linkStyle = { color: '#FF5C00', textDecoration: 'underline' };
const linkAccentStyle = { color: '#FF5C00', textDecoration: 'underline', fontWeight: 600 };

const mapStyle = {
  position: 'absolute' as const,
  inset: 0,
  background: '#0A0608',
};

const footerStyle = {
  position: 'absolute' as const,
  // Match the FAB and Legend chip's bottom-edge baseline so the three
  // bottom-row affordances form a visually aligned strip:
  //   Sources (this) · Report litter FAB · Legend chip
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  left: 10,
  zIndex: 800,
  // Cap to ~60vw so the collapsed "Sources ▾" chip stays small on
  // phones; on desktop the open form (with all the long agency names)
  // fits comfortably below this cap.
  maxWidth: 'min(60vw, 480px)',
  background: 'rgba(10, 6, 8, 0.92)',
  border: '1px solid #3a2e30',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: 11,
  color: '#aaa',
  pointerEvents: 'auto' as const,
};

const sourcesSummaryStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  cursor: 'pointer',
  userSelect: 'none' as const,
  listStyle: 'none',
  fontWeight: 600 as const,
  letterSpacing: '0.04em',
  color: '#b8a878',
};

const sourcesChevronStyle = {
  fontSize: 11,
  color: '#b8a878',
  display: 'inline-block',
};

const sourcesBodyStyle = {
  marginTop: 6,
  // Long agency-name links wrap rather than overflow; prevents the
  // "EPA Wate..." truncation seen on iPhone PWA.
  overflowWrap: 'anywhere' as const,
};

const errorStyle = {
  padding: 12,
  background: '#3a1a1a',
  color: '#ff9a9a',
  fontSize: 13,
};


// ─── Persistent "Report litter" CTA ──────────────────────────────────────
// Fixed TOP-right CTA. Always visible, independent of obsCount —
// civic-tech products fail when the contribute affordance hides itself as
// the map fills in.
//
// Top-right was chosen because the legend (open by default, bottom-right)
// and Leaflet's control container (z-index 1000) own the bottom-right
// corner. Hook + zoom controls live top-left. Top-right is the only
// uncontested corner.

function ContributeFab() {
  const { user } = useAuth();

  function onClick() {
    if (user) {
      window.location.assign('/submit');
      return;
    }
    // Capture current pathname+search so the user lands back here
    // after the OAuth round-trip. The callback lambda re-validates
    // same-origin, so a malicious `next` is defanged server-side too.
    const here = window.location.pathname + window.location.search;
    window.location.assign('/sign-in?next=' + encodeURIComponent(here));
  }

  return (
    <>
      {/* Pending-review count chip next to the FAB (contract §7). The
          badge renders nothing when signed out or the queue is empty. */}
      <QueueBadge className="queue-badge--fab" />
      <LabelQueueBadge className="queue-badge--fab" />
      <button
        type="button"
        onClick={onClick}
        style={fabStyle}
        aria-label="Report litter (contribute a new observation)"
      >
        <span aria-hidden="true" style={fabIconStyle}>+</span>
        <span>Report litter</span>
      </button>
    </>
  );
}

const fabStyle = {
  position: 'fixed' as const,
  // Bottom-center: standard mobile primary-CTA position, thumb-reachable.
  // Pulls clear of the home indicator via env(safe-area-inset-bottom) +
  // a 16px gap. translateX(-50%) centers against the FAB's own width
  // regardless of its content length.
  left: '50%',
  bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
  transform: 'translateX(-50%)',
  // Leaflet's bottom-right control container is z-index 1000; the FAB
  // must clear that so its tap area isn't intercepted.
  zIndex: 1100,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '12px 18px',
  borderRadius: 999,
  border: '1px solid #c24b1a',
  background: '#FF5C00',
  color: '#1b0803',
  fontFamily: 'system-ui, sans-serif',
  fontSize: 15,
  fontWeight: 700 as const,
  letterSpacing: '0.01em',
  cursor: 'pointer',
  boxShadow: '0 6px 20px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 92, 0, 0.25)',
  transition: 'transform 120ms ease-out, box-shadow 120ms ease-out',
};

const fabIconStyle = {
  fontSize: 20,
  lineHeight: 1,
  fontWeight: 800 as const,
};
